/* Humbler bag: one renderer for the drawer (dialog#hb-cart) and the /cart page ([data-hb-cart-page]),
   two adapters with one normalized model (shaped like Shopify's /cart.js + `offers` + per-line labels).
   - Native adapter (below): Shopify AJAX cart. Shopify's prices and totals are the truth; HumblerOffers
     only supplies hints (gift progress, $29 third piece, mystery unlock, "add one more" nudges). It also
     runs the live checks: free-gift add/verify/remove, and the offer adds (the $29 3rd piece, a chain set) rise
     the bag by no more than the engine says.
   - Preview adapter: assets/humbler-preview.js registers window.HumblerCartAdapter (session storage,
     priced by HumblerOffers) on local preview pages; when present it replaces the native adapter.
   API (every method returns a Promise): window.HumblerCart.add(items, options) -> { ok, error?, cart },
   open(trigger?), close(), refresh() -> cart, change(key, quantity) / remove(key) / removeLines(keys) /
   restoreGift() -> { ok, error?, cart }, getState() -> { cart, busy, adapter }.
   add() first runs the personalisation guard (refusal() below): an add it refuses is never posted and its promise
   rejects with an Error whose message the caller shows (code 'engraving' or 'personalise'). Items may carry hints
   the guard reads and Shopify never sees: kind (snippets/humbler-personalised: 'plain' when the page classified the
   piece), productId, handle, type.
   Dispatches document 'humbler:cart:updated' { item_count, cart } after every change.
   Checkout+ (priority dispatch + package protection, the store's own hidden product): a bag with it switched on shows an
   info card and two checkouts, "Checkout+ · total + fee" and a plain "Checkout without …"; the submit handler adds or
   removes the protection line (adapter.setProtection), then submits as before. Nothing is ever pre-added. Its line is
   never merchandise: the model both adapters return goes through merchandise(), so cart.items, item_count and
   total_price leave it out and cart.protection describes it.
   Render order (drawer and /cart): offer progress → Checkout with the total (Liquid) → lines (set cards, singles,
   Mystery add-on line, gift) → Mystery add-on card → one offers row (third piece > add one more > "Complete your
   set"; the drawer skips the last once the gift is unlocked) → order summary card (value rows, total + savings, Shop
   Pay installments line) → Checkout + Shop Pay. JAXXON's proportions (docs/references/jaxxon.md §5): grey cards,
   96px 4:5 photos, 15px names, 13px detail lines, 38px steppers, 128px+ offer cards. Values come from line prices
   only; the UI never prices anything.
   The drawer has a second layout, Ridge (.hb-cart--ridge, applyLayout() below): the same markup in another order and
   size, with the offers moved into a panel beside the bag from 1000px (placeOffers()). */
(() => {
  if (window.HumblerCart) return;

  const routesRoot = window.Shopify?.routes?.root || '/';
  const route = path => `${routesRoot}${path}`;
  const DECLINED_KEY = 'humbler:gift-declined';
  const EXCLUDED = /navidium|shipping protection|package protection|order protection|\broute\b/i;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- page data ---------- */
  const readJson = selector => {
    try { return JSON.parse(document.querySelector(selector)?.textContent || 'null'); } catch { return null; }
  };
  const raw = readJson('#hb-offers-config') || {};
  // Shopify HTML-escapes translations (their own text too), so the strings arrive as entities ("&#39;"); they are
  // shown with textContent, so the five entities its escape writes are decoded once here (&amp; last).
  const ENTITIES = { '&#39;': "'", '&quot;': '"', '&lt;': '<', '&gt;': '>', '&amp;': '&' };
  const decodeText = value => String(value).replace(/&(#39|quot|lt|gt|amp);/g, match => ENTITIES[match]);
  const strings = Object.fromEntries(Object.entries(readJson('[data-hb-cart-strings]') || {}).map(([key, value]) => [key, typeof value === 'string' ? decodeText(value) : value]));
  // Drawer section settings: Shop Pay installments line, gift line image, best sellers (fallback picks).
  const settingsData = readJson('[data-hb-cart-settings]') || {};
  const icons = {};
  document.querySelectorAll('template[data-hb-cart-icons]').forEach(template => {
    template.content.querySelectorAll('[data-icon]').forEach(node => { icons[node.dataset.icon] = node.innerHTML.trim(); });
  });

  // Checkout+ (snippets/humbler-offers-config "protection"): on only when switched on in theme settings, its product
  // chosen and on sale. Off (the live store today), every protection path below is a no-op and the bag is unchanged.
  const protection = {
    enabled: !!(raw.protection?.enabled && raw.protection.variantId && raw.protection.available !== false),
    variantId: String(raw.protection?.variantId ?? ''),
    handle: String(raw.protection?.handle || ''),
    price: Number(raw.protection?.price) || 0
  };
  const isProtectionLine = item => protection.enabled && String(item?.variant_id ?? '') === protection.variantId;

  // Theme-settings offer config (snippets/humbler-offers-config) -> offer engine config.
  // Settings amounts (pair/chains/third prices, gift and free-shipping thresholds) are USD cents; Shopify's
  // cart is in the shopper's currency, so they are converted with Shopify's rate (HumblerUI.fromUsd, the
  // rate Shopify applies to discount fixed amounts) and every hint compares like with like. Product
  // prices in the config (mystery price, gift value, Checkout+ price) are already in the shopper's currency. The
  // engine itself stays currency-agnostic.
  function engineConfig(source, convert = cents => cents) {
    const off = part => (part && part.enabled === false ? { ...part, handles: [] } : part);
    const variant = entry => {
      const [handle, id] = String(entry).split(':').map(value => value.trim());
      return handle && id ? { handle, variantId: id } : null;
    };
    const usd = window.HumblerOffers.normalizeConfig({
      pair: off(source.pair),
      chains: off(source.chains),
      stack: off(source.stack),
      third: source.third && { price: source.third.price, variants: (source.third.variants || []).map(variant).filter(Boolean), any: source.third.any === true },
      gift: source.gift,
      mystery: source.mystery,
      shipping: source.freeShipping,
      protection: protection.enabled ? protection : undefined
    });
    return {
      ...usd,
      pair: { ...usd.pair, price: convert(usd.pair.price) },
      chains: { ...usd.chains, price: convert(usd.chains.price), unitPrice: convert(usd.chains.unitPrice) },
      stack: { ...usd.stack, price: convert(usd.stack.price) },
      third: { ...usd.third, price: convert(usd.third.price), priceUsd: usd.third.price },
      gift: { ...usd.gift, threshold: convert(usd.gift.threshold) },
      shipping: { ...usd.shipping, threshold: convert(usd.shipping.threshold) }
    };
  }
  /* ---------- small helpers ---------- */
  const UI = () => window.HumblerUI || {};
  const fromUsd = cents => (UI().fromUsd ? UI().fromUsd(cents) : cents);
  // Another currency: Shopify rounds converted prices, so the live checks allow a little slack.
  const localized = () => !!UI().localized;
  const cfg = window.HumblerOffers ? engineConfig(raw, fromUsd) : null;
  const gift = {
    enabled: !!(cfg && raw.gift?.variantId && raw.gift.available !== false),
    title: raw.gift?.title || '',
    // Its name in offer copy (Theme settings › Humbler offers › "Free gift name in offer copy", e.g. "cross pendant");
    // the gift's own bag line keeps its product title.
    label: String(raw.gift?.label || '').trim(),
    value: Number(raw.gift?.value) || 0
  };
  const pairHandles = new Set(cfg?.pair.handles || []);

  const money = cents => (UI().money ? UI().money(cents) : `$${(Number(cents) / 100).toFixed(2).replace(/\.00$/, '')}`);
  const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const imageUrl = (src, width) => (UI().imageUrl ? UI().imageUrl(src, width) : src || '');
  const icon = name => icons[name] || '';
  const sum = values => values.reduce((total, value) => total + value, 0);
  const t = (key, vars = {}) => String(strings[key] ?? key).replace(/\{(\w+)\}|%(\w+)%/g, (match, brace, pct) => ((brace || pct) in vars ? vars[brace || pct] : match));
  // The chain deal is a chain's converted $33 three times, as checkout charges it (never a converted $99: a cent apart in
  // some currencies).
  const prices = cfg ? { pair: money(cfg.pair.price), chains: money(cfg.chains.unitPrice * cfg.chains.count), third: money(cfg.third.price), stack: money(cfg.stack.price) } : {};
  const session = {
    get: key => { try { return sessionStorage.getItem(key); } catch { return null; } },
    set: (key, value) => { try { sessionStorage.setItem(key, value); } catch { /* storage blocked */ } },
    remove: key => { try { sessionStorage.removeItem(key); } catch { /* storage blocked */ } }
  };
  const failure = (code, message) => Object.assign(new Error(message || code), { code });
  const isGiftLine = item => item.properties?._free_gift === 'true' || item.properties?._free_gift === true;
  const firstAvailable = product => product?.variants?.find(variant => variant.available) || null;

  // Same cleanup as snippets/humbler-product-title (compact): drop a trailing finish label, then a trailing
  // category ("Double Sided Pendant", "Coin Pendant", "Pendant", "Bracelet", "Ring"), so line titles fit one line;
  // all-caps catalog titles ("3MM CUBAN LINK CHAIN") read as "3mm Cuban Link Chain", like the cards.
  // Bag lines keep the category in their mono meta line (splitTitle), so pieces of one design stay told apart.
  const FINISH_SUFFIX = /\s+(?:[-–—|]\s*|\()(?:rose gold|gold|silver|black|platinum)\)?$/i;
  const CATEGORY_SUFFIX = /\s+(double sided pendant|coin pendant|pendant|bracelet|ring)$/i;
  const CATEGORY_LABELS = { 'double sided pendant': 'Pendant', 'coin pendant': 'Coin pendant', pendant: 'Pendant', bracelet: 'Bracelet', ring: 'Ring' };
  function displayCase(title) {
    let text = String(title ?? '').trim();
    if (/[A-Z]/.test(text) && text === text.toUpperCase()) {
      text = text.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    return text.replace(/(\d)mm\b/gi, '$1mm');
  }
  function splitTitle(title) {
    const clean = String(title ?? '').trim().replace(FINISH_SUFFIX, '').trim();
    const match = clean.match(CATEGORY_SUFFIX);
    const name = match ? clean.slice(0, match.index).trim() : '';
    return name ? { name: displayCase(name), category: CATEGORY_LABELS[match[1].toLowerCase()] || '' } : { name: displayCase(clean), category: '' };
  }
  const compactTitle = title => splitTitle(title).name;
  // One design across its finishes: the name and the category, so gold and silver twins merge but a ring or bracelet
  // never merges with the pendant that shares its name ("Memento Mori" ring, bracelet and pendant are three designs).
  const designOf = title => { const { name, category } = splitTitle(title); return `${name}|${category}`.toLowerCase(); };
  // The free gift as offer copy names it ("Unlocks a free cross pendant"): the setting, else its short title.
  const giftName = () => gift.label || compactTitle(gift.title) || gift.title;
  // The full display title (snippets/humbler-product-title without compact; displayTitle in
  // assets/humbler-search.js, the Spotlight rows): finish label dropped, all caps to Title Case, "3mm".
  // For what the bag says and announces; line items sent to Shopify keep their own titles.
  const displayTitle = title => {
    const text = String(title ?? '').trim();
    return displayCase(text.replace(FINISH_SUFFIX, '').trim() || text);
  };

  // Product data (/products/<handle>.js, recommendations or preview data) -> one small shape.
  function normalizeProduct(product) {
    if (!product) return null;
    const absolute = src => (typeof src === 'string' && src.startsWith('//') ? `https:${src}` : src || null);
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      url: product.url || `${route('products/')}${product.handle}`,
      // Product type and the theme's personalised kind (best sellers carry it): what personalKind() reads.
      type: product.type ?? product.product_type ?? '',
      kind: product.kind || '',
      image: absolute(typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src || product.image || product.images?.[0]),
      available: product.available !== false,
      // Collection handles (the preview catalog has them; /products/<handle>.js does not): the finish source.
      collections: Array.isArray(product.collections) ? product.collections : null,
      // Option names ("Size", "Length"): the bag's 3rd-piece size menu labels itself with the first.
      options: (product.options || []).map(option => (typeof option === 'string' ? option : option?.name)).filter(Boolean),
      variants: (product.variants || []).map(variant => ({
        id: variant.id,
        title: variant.title,
        price: Number(variant.price),
        compare_at_price: variant.compare_at_price ? Number(variant.compare_at_price) : null,
        available: variant.available !== false,
        image: absolute(variant.featured_image?.src || variant.image || null)
      }))
    };
  }

  /* ---------- personalised products (docs/DOG-TAG-CUSTOMISER-PLAN.md §7, snippets/humbler-personalised) ---------- */
  // The engraving line property: the key every live order for the custom dog tag already carries, so fulfilment
  // sees no change. Its value is the quote in capitals (assets/humbler-engraving.js); the bag labels it "Engraving".
  const ENGRAVING_KEY = 'text-box-1';
  const ENGRAVING_MAX = 40;
  // A saved engraving's characters (lead decision 4, as assets/humbler-engraving.js allows them, in capitals): so the
  // bag refuses emoji, accented letters, ẞ and the curly ’ on every page, with or without that script.
  const ENGRAVING_CHARS = /^[A-Z0-9 .,'!?&-]+$/;
  // Paths that add in one tap and so cannot personalise anything: product cards, the landing list and the empty-bag
  // lists, bag picks ('card'), the $29 third piece ('third') and the Mystery add-on ('mystery').
  const ONE_TAP = new Set(['card', 'third', 'mystery']);
  // #hb-engraving-config (layout/theme.liquid, from Theme settings › humbler_engraving_products):
  // { key, products: [product ids], variants: [variant ids], builders: [CPB product ids] }. Read on each use, so the
  // guard never depends on assets/humbler-engraving.js loading; null when missing or unreadable.
  function engravingConfig(doc) {
    try {
      const json = JSON.parse(doc?.getElementById('hb-engraving-config')?.textContent || 'null');
      if (!json || typeof json !== 'object') return null;
      const ids = list => new Set((Array.isArray(list) ? list : []).map(String));
      return { products: ids(json.products), variants: ids(json.variants), builders: ids(json.builders) };
    } catch { return null; }
  }
  // A line's engraving as the bag shows it ('' when it has none). Lines carried over from older themes show theirs
  // as typed.
  const engravingOf = properties => {
    const value = properties ? properties[ENGRAVING_KEY] : '';
    return typeof value === 'string' ? value.trim() : '';
  };
  // An engraving as the product page saves it: only the allowed capitals, digits and marks, single spaces, trimmed, at
  // most 40 characters; and valid by the engraving script's own rules (window.HumblerEngraving.validate, which also
  // checks the 2-line width) when that script is on the page.
  function engravingSaved(value, engraving) {
    if (typeof value !== 'string' || !ENGRAVING_CHARS.test(value) || value !== value.trim() || / {2}/.test(value) || value.length > ENGRAVING_MAX) return false;
    if (typeof engraving?.validate !== 'function') return true;
    try {
      const result = engraving.validate(value);
      return !!result?.ok && (result.value === undefined || result.value === value);
    } catch { return false; }
  }
  // A product's snippets/humbler-personalised kind, for bag picks and one-tap adds: 'hidden' (a CPB order record),
  // 'engraving', 'builder', 'plain', or 'unknown' when the engraving list cannot be read and the page did not
  // classify the piece (then it is offered as a link, never a one-tap add).
  function personalKind(product, config) {
    if (!product) return 'unknown';
    if (product.type === 'cpb_hybrid' || /^cpb-order-/.test(String(product.handle || ''))) return 'hidden';
    if (product.kind === 'hidden' || product.kind === 'engraving' || product.kind === 'builder') return product.kind;
    if (product.id !== undefined && config?.products.has(String(product.id))) return 'engraving';
    if (product.type === 'cpb_product' || (product.id !== undefined && config?.builders.has(String(product.id)))) return 'builder';
    return config || product.kind === 'plain' ? 'plain' : 'unknown';
  }
  // Offer variants from the theme's offer settings (snippets/humbler-offers-config): the Mystery piece, the $29 third
  // pieces and the free gift. A one-tap add of one of these is classified even without the engraving list.
  const offerVariantIds = source => new Set([source?.mystery?.variantId, source?.gift?.variantId, ...(source?.third?.variants || []).map(entry => String(entry).split(':')[1])]
    .filter(value => value !== undefined && value !== null && value !== '').map(value => String(value).trim()));
  // The bag's guard on every add (a refused add is never posted): an engraving variant needs a saved engraving in its
  // 'text-box-1' property and no other property (fulfilment reads only that key; never an Easify field), from any
  // path; a one-tap path adds nothing personalised; and without a readable #hb-engraving-config a one-tap add needs
  // a piece the page classified as plain (data-hb-kind) or an offer variant. Product forms (product pages, set
  // builders) work as before without the config. Returns '' (allowed), 'engraving' (no saved engraving, or other
  // properties beside it) or 'personalise' (a one-tap add of something that must be personalised on its page).
  function refusal(items, source, config, offerIds, engraving) {
    const oneTap = ONE_TAP.has(source);
    for (const item of items) {
      const id = String(item.id);
      const hint = item.hint || {};
      const engravingVariant = !!config && config.variants.has(id);
      if (engravingVariant && oneTap) return 'personalise';
      if (engravingVariant && !engravingSaved(item.properties?.[ENGRAVING_KEY], engraving)) return 'engraving';
      if (engravingVariant && Object.keys(item.properties || {}).some(key => key !== ENGRAVING_KEY)) return 'engraving';
      if (!oneTap) continue;
      const kind = personalKind({ id: hint.productId, handle: hint.handle, type: hint.type, kind: hint.kind }, config);
      if (kind === 'hidden' || kind === 'engraving' || kind === 'builder') return 'personalise';
      if (kind !== 'plain' && !offerIds.has(id)) return 'personalise';
    }
    return '';
  }

  // An offer add (the $29 3rd piece, a 3-chain set) held its price when the bag total rose by no more than the offer
  // engine says it adds: the engine prices the bag as the store's discount functions do (assets/humbler-offers.js), so a
  // 3rd after a $99 set adds $29 (a $27 bracelet $27) and three chains add $99, or what the cheaper way costs with the
  // rest of the bag; more means the discount did not apply.
  const thirdPriceHeld = (beforeTotal, afterTotal, expectedRise, slack = 0) => afterTotal - beforeTotal <= expectedRise + slack;

  /* ---------- native adapter ---------- */
  function nativeKind(item) {
    if (isProtectionLine(item)) return 'protection';
    if (isGiftLine(item)) return 'gift';
    if (item.properties?._bundle_upsell) return 'third';
    if (cfg && item.handle === cfg.mystery.handle) return 'mystery';
    return 'product';
  }
  const excludedFromSpend = item => item.kind === 'gift' || item.kind === 'protection' || item.gift_card === true || (cfg && item.handle === cfg.gift.handle) ||
    EXCLUDED.test([item.product_title, item.product_type, item.vendor].filter(Boolean).join(' '));

  // Bag lines (either adapter's model) -> offer engine lines, at regular prices. The store's 2-for-$99 function reads
  // one line property, the set the shopper built (_bundle_id): each built set is a $99 set.
  const engineLines = items => items.map(item => ({
    key: item.key, handle: item.handle, variantId: item.variant_id, productId: item.product_id, quantity: item.quantity, price: item.original_price,
    kind: item.kind === 'gift' || item.kind === 'third' ? item.kind : 'product', bundle: item.properties?._bundle_id || '',
    title: item.product_title, productType: item.product_type, vendor: item.vendor, giftCard: item.gift_card === true
  }));

  // A stack the shopper broke: lines still labelled by the stack builder (_bundle_id "stack-…", _bundle_offer, _bundle_item)
  // whose _bundle_id no longer holds what the store's function prices as a stack: k of the stack pendants with 2k of the
  // chains and nothing else (scripts/supaeasy/pendant-stack-95.js), because one of its lines was removed or stepped.
  // The function prices such lines as loose pieces already; the labels would still call the order a stack (order
  // #69205, 2026-10-23: a pendant and one chain labelled "Pendant stack", the Flow tag pendant-stack on it), so
  // settle() blanks them. The shape is read with the theme's own handle lists (raw, before an offer switch empties
  // them: a stack is a stack while its offer is paused), never the engine's grouping; a bundle Shopify itself discounted
  // as the stack is whole whatever the lists say; no lists, no judgement.
  const STACK_LABELS = ['_bundle_id', '_bundle_offer', '_bundle_item'];
  const handlesOf = part => new Set(Array.isArray(part?.handles) ? part.handles.map(entry => String(entry).trim()).filter(Boolean) : []);
  const stackPendantHandles = handlesOf(raw.stack);
  const stackChainHandles = handlesOf(raw.chains);
  const stackPriced = item => !!cfg && (item.line_level_discount_allocations || []).some(entry => String(entry?.discount_application?.title || '').toLowerCase() === String(cfg.stack.label).toLowerCase());
  function brokenStackLines(current) {
    if (!stackPendantHandles.size || !stackChainHandles.size) return [];
    const bundles = new Map();
    for (const item of current?.items || []) {
      const id = String(item.properties?._bundle_id || '');
      if (!/^stack-/.test(id)) continue;
      if (!bundles.has(id)) bundles.set(id, { lines: [], pendants: 0, chains: 0, others: 0, priced: false });
      const set = bundles.get(id);
      set.lines.push(item);
      if (item.kind === 'product' && stackPendantHandles.has(item.handle)) set.pendants += item.quantity;
      else if (item.kind === 'product' && stackChainHandles.has(item.handle)) set.chains += item.quantity;
      else set.others += item.quantity;
      if (stackPriced(item)) set.priced = true;
    }
    return [...bundles.values()].filter(set => !set.priced && !(set.pendants > 0 && set.chains === 2 * set.pendants && set.others === 0)).flatMap(set => set.lines);
  }
  // The line's properties with the stack labels blanked: Shopify keeps a line's properties through cart/change.js (an
  // empty object changes nothing) and takes blank values, which every reader (the functions, the Flow, this bag) treats
  // as no label.
  const unlabelled = line => ({ ...(line.properties || {}), ...Object.fromEntries(STACK_LABELS.map(key => [key, ''])) });

  // The offer behind a line Shopify discounted: the shopper's own set properties first, then the store's discount
  // title, then the engine's grouping (equal-price ties prefer the shopper's own sets, then line key, so it can
  // still pair across sets the properties do not name). One offer is always one label, so it gives one value row.
  function nativeOffer(item, engine) {
    const properties = item.properties || {};
    const titles = (item.line_level_discount_allocations || []).map(entry => String(entry.discount_application?.title || '').trim().toLowerCase());
    if (properties._three_chain_offer) return 'chains';
    // A line added as the $29 piece is that piece only while the engine still prices it so: once its set lost a
    // piece it pairs with what is left (as the store prices it), so the card does not still say "Set of 3". Before
    // _bundle_id: the bag gives a 3rd its set's _bundle_id.
    if (item.kind === 'third') return engine.includes('third') ? 'third' : ['pair', 'chains'].find(name => engine.includes(name)) || 'third';
    // A pendant stack and the chain deal price built sets' lines too: their titles win over the set's _bundle_id.
    if (titles.includes(String(cfg.stack.label).toLowerCase())) return 'stack';
    if (titles.includes(String(cfg.chains.label).toLowerCase())) return 'chains';
    if (properties._bundle_id) return engine.includes('stack') ? 'stack' : 'pair';
    if (titles.includes(String(cfg.pair.label).toLowerCase())) return 'pair';
    return ['stack', 'chains', 'pair', 'third'].find(name => engine.includes(name)) || null;
  }

  // Shopify cart -> normalized model. Offer engine output is used for hints and labels only; spend,
  // thresholds and line labels follow Shopify's own final prices.
  function nativeModel(cart, { declined, notice } = {}) {
    const items = (cart.items || []).map(item => ({ ...item, kind: nativeKind(item) }));
    let offers = null;
    if (cfg) {
      offers = window.HumblerOffers.price(engineLines(items), cfg, { giftDeclined: declined, mysteryPrice: raw.mystery?.price });
      const spend = Math.max(0, cart.total_price - sum(items.filter(excludedFromSpend).map(item => item.final_line_price)));
      const hint = offers.gift;
      hint.spend = spend;
      hint.remaining = Math.max(0, hint.threshold - spend);
      hint.qualifies = items.some(item => !excludedFromSpend(item)) && spend >= hint.threshold;
      hint.shouldAdd = hint.qualifies && !hint.inCart && !declined && !hint.owned;
      hint.shouldRemove = hint.inCart && !hint.qualifies;
      // Free shipping on the merchandise: Shopify's total less a Checkout+ line.
      const merchandiseTotal = cart.total_price - sum(items.filter(item => item.kind === 'protection').map(item => item.final_line_price));
      offers.shipping = { threshold: cfg.shipping.threshold, remaining: Math.max(0, cfg.shipping.threshold - merchandiseTotal), qualifies: merchandiseTotal >= cfg.shipping.threshold };
      offers.third.unlocksGift = offers.third.available && spend < hint.threshold && spend + offers.third.rise >= hint.threshold;
      offers.mystery.unlocksGift = offers.mystery.unlocksGift && spend < hint.threshold && spend + offers.mystery.price >= hint.threshold;
      const outOf = item => offers.lines.find(line => line.key === item.key);
      const discountedLine = item => item.final_line_price < item.original_line_price && item.kind !== 'protection' && item.kind !== 'gift';
      const groupsOf = item => (outOf(item)?.offers || []).filter(entry => entry.offer === 'pair' || entry.offer === 'chains' || entry.offer === 'stack').map(entry => entry.groupId);
      // A deal piece whose share of the saving rounds to nothing (the store's function spreads one saving over the deal's
      // pieces by price) shows no discount on Shopify, yet it is one of the deal's pieces: it joins the deal's card once
      // Shopify discounted another of its pieces (the deal really applied), as the preview's card shows it. A piece the
      // engine prices lower but Shopify did not (the store's discount missed it) stays a row of its own.
      const applied = new Set(items.filter(discountedLine).flatMap(groupsOf));
      // Equal prices on several lines: the store may discount a different one of them than the engine picked (the piece
      // left at its own price). A discounted 2-for-$99 line the engine left out is the deal's all the same.
      const deal = offers.groups.find(group => group.offer === 'pair')?.id || null;
      for (const item of items) {
        const out = outOf(item);
        const discounted = discountedLine(item);
        const member = !discounted && item.kind !== 'gift' && item.kind !== 'protection' && (out?.offers || []).some(entry => entry.offer === 'pair' && entry.amount === 0 && applied.has(entry.groupId));
        const missed = discounted && deal && !groupsOf(item).length && item.kind !== 'mystery' && pairHandles.has(item.handle);
        const labels = missed ? [...(out?.labels || []), 'pair'] : out?.labels || [];
        const offer = item.kind === 'gift' ? (item.final_line_price === 0 ? 'gift' : null) : discounted || member ? nativeOffer(item, labels) : null;
        item.offer_labels = offer ? [offer] : [];
        item.offer_groups = discounted || member ? (missed ? [deal] : groupsOf(item)) : [];
      }
    }
    return { ...cart, items, offers, notice: notice || null };
  }

  function nativeAdapter() {
    let initial = readJson('[data-hb-cart-initial]');
    let last = null;
    let failedGiftSpend = null;
    // The broken-stack lines a settle could not unlabel (their keys): not retried until the cart's lines change.
    let failedUnlabel = '';
    const products = new Map();
    const productQueue = [];
    let productsLoading = 0;
    const nextProduct = () => {
      while (productsLoading < 4 && productQueue.length) {
        const job = productQueue.shift();
        productsLoading += 1;
        job().finally(() => { productsLoading -= 1; nextProduct(); });
      }
    };
    const queued = task => new Promise((resolve, reject) => { productQueue.push(() => task().then(resolve, reject)); nextProduct(); });
    const related = new Map();

    async function request(path, body) {
      const options = body === undefined
        ? { headers: { Accept: 'application/json' } }
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) };
      const response = await fetch(route(path), options);
      let json = null;
      try { json = await response.json(); } catch { json = null; }
      if (!response.ok) throw failure('shopify', json?.description || json?.message);
      return json;
    }
    const fetchCart = () => request('cart.js');
    const setQuantity = (key, quantity) => request('cart/change.js', { id: key, quantity });
    const declined = () => session.get(DECLINED_KEY) === '1';
    const model = (cart, notice) => (last = nativeModel(cart, { declined: declined(), notice }));

    // Live gift lifecycle: add when the bag qualifies (unless declined) and verify it is free,
    // remove it when the bag no longer qualifies.
    async function settle(cart, notice) {
      let current = model(cart, notice);
      // Checkout+ is one line at quantity 1 and never stays in a bag with nothing left to protect (a shopper back from
      // checkout who empties the bag). Never added here: only the Checkout+ button adds it (setProtection).
      const covers = current.items.filter(item => item.kind === 'protection');
      if (covers.length) {
        const hasMerchandise = current.items.some(item => item.kind !== 'protection' && item.kind !== 'gift');
        if (!hasMerchandise || covers.length > 1 || covers[0].quantity > 1) {
          cart = await request('cart/update.js', { updates: Object.fromEntries(covers.map((line, index) => [line.key, hasMerchandise && index === 0 ? 1 : 0])) });
          current = model(cart, notice);
        }
      }
      // A broken stack's leftover lines get their stack labels blanked, one cart/change.js per line (quantity and other
      // properties kept): idempotent, nothing to lose, no stock needed. It acts on a fresh cart (never the page's
      // server-rendered snapshot), each change addressed by the key the previous answer gave the line (keys change as the
      // cart is repriced). The budget is fixed up front (each line once, plus 3 retries from a fresh cart); lines still
      // labelled after it are not retried until the cart's lines change (failedUnlabel), so a stuck change never costs
      // every later settle a burst of requests.
      const signature = lines => lines.map(line => line.key).sort().join('|');
      let leftover = brokenStackLines(current);
      if (leftover.length && signature(leftover) !== failedUnlabel) {
        cart = await fetchCart();
        current = model(cart, notice);
        leftover = brokenStackLines(current);
        const budget = leftover.length + 3;
        for (let attempt = 0; leftover.length && attempt < budget; attempt += 1) {
          const line = leftover[0];
          try { cart = await request('cart/change.js', { id: line.key, quantity: line.quantity, properties: unlabelled(line) }); }
          catch { cart = await fetchCart(); }
          current = model(cart, notice);
          leftover = brokenStackLines(current);
        }
        failedUnlabel = leftover.length ? signature(leftover) : '';
      }
      if (!gift.enabled || !current.offers) return current;
      const hint = current.offers.gift;
      const giftLines = current.items.filter(item => item.kind === 'gift');
      if (giftLines.length && !hint.qualifies) {
        for (const line of giftLines) cart = await setQuantity(line.key, 0);
        return model(cart, notice);
      }
      if (giftLines.length > 1 || giftLines[0]?.quantity > 1) {
        for (const [index, line] of giftLines.entries()) cart = await setQuantity(line.key, index === 0 ? 1 : 0);
        return model(cart, notice);
      }
      if (hint.shouldAdd && failedGiftSpend !== hint.spend) {
        let added = true;
        try {
          await request('cart/add.js', { items: [{ id: Number(raw.gift.variantId), quantity: 1, properties: { ...(raw.gift.properties || {}), _free_gift: 'true' } }] });
        } catch { added = false; }
        cart = await fetchCart();
        const line = cart.items.find(isGiftLine);
        if (!added || !line || line.final_line_price !== 0) {
          if (line) cart = await setQuantity(line.key, 0);
          failedGiftSpend = hint.spend;
          return model(cart, notice || { code: 'gift' });
        }
        failedGiftSpend = null;
        current = model(cart, notice);
      }
      return current;
    }

    return {
      name: 'native',
      checkout: true,
      async getCart() {
        const cart = initial || await fetchCart();
        initial = null;
        return settle(cart);
      },
      async add(items, options = {}) {
        const third = options.source === 'third';
        const chains = options.source === 'chains';
        const code = options.discountCode ? String(options.discountCode).trim() : '';
        const before = third || chains || code ? await fetchCart() : null;
        const response = await request('cart/add.js', {
          items: items.map(item => ({ id: Number(item.id), quantity: item.quantity, ...(item.properties ? { properties: item.properties } : {}) }))
        });
        const addedItems = (response?.items || [response]).filter(Boolean);
        let cart;
        if (code) {
          const codes = new Set((before.discount_codes || []).map(entry => entry.code).filter(Boolean));
          codes.add(code);
          cart = await request('cart/update.js', { discount: [...codes].join(',') });
        } else {
          cart = await fetchCart();
        }
        if (before) {
          // Lines are matched by variant and properties: keys change as the cart is repriced.
          const sameLine = (a, b) => String(a.variant_id) === String(b.variant_id) && JSON.stringify(a.properties || {}) === JSON.stringify(b.properties || {});
          const lines = cart.items.filter(item => addedItems.some(added => sameLine(added, item)));
          const previous = line => before.items.find(item => sameLine(item, line))?.quantity || 0;
          const addedUnits = line => Math.max(0, line.quantity - previous(line));
          // The offer engine prices both carts as the store's functions do (the cheaper way for chains included), so the
          // bag may rise by what it says the new lines add. In another currency the amounts are conversions and Shopify
          // rounds the converted product prices, so the check allows 10% of at least the 3rd-piece price there (a failed
          // discount is far off, e.g. a full price); in the shop's currency a cent per line.
          const slack = amount => (localized() ? Math.round(amount * 0.1) : 0);
          const engineTotal = shopifyCart => window.HumblerOffers.price(engineLines(shopifyCart.items.map(item => ({ ...item, kind: nativeKind(item) }))), cfg).total;
          const expected = engineTotal(cart) - engineTotal(before);
          const verified = lines.some(line => addedUnits(line) > 0) && thirdPriceHeld(before.total_price, cart.total_price, expected, Math.max(lines.length, slack(Math.max(expected, cfg.third.price))));
          if (!verified) {
            // Roll back only what this call added (live behaviour), in one cart/update.js with this cart's keys: keys
            // change as the cart is repriced (removing one line re-keys the rest), so a line at a time failed part-way.
            // A line this add merged into goes back to the quantity it had before.
            const updates = Object.fromEntries(lines.map(line => [line.key, previous(line)]));
            cart = await request('cart/update.js', { updates });
            await settle(cart);
            throw failure(third ? 'third' : 'chains');
          }
        }
        return settle(cart);
      },
      async change(key, quantity) {
        const item = last?.items.find(line => line.key === key);
        if (item?.kind === 'gift' && quantity <= 0) session.set(DECLINED_KEY, '1');
        // The gift stays at 1. A 3rd piece steps like any line: more pieces simply join the deal at the same price.
        if (item?.kind === 'gift') quantity = Math.min(quantity, 1);
        return settle(await setQuantity(key, Math.max(0, quantity)));
      },
      // "Remove set": every line of the set in one cart/update.js request, then one gift check. Line keys are not
      // stable: removing one line of a discounted set re-prices the others and Shopify gives them new keys, so a
      // change per line failed part-way ("no valid id or line parameter") and left half a set.
      async removeMany(keys) {
        const cart = await request('cart/update.js', { updates: Object.fromEntries(keys.map(key => [key, 0])) });
        return settle(cart);
      },
      // Checkout+ on: its variant once (cart/add.js), only when the bag has none. Off: every protection line in one
      // cart/update.js. Keys change as the cart is repriced, so the lines come from a fresh cart.
      async setProtection(on) {
        let cart = await fetchCart();
        const covers = (cart.items || []).filter(isProtectionLine);
        if (on && protection.enabled && !covers.length) {
          await request('cart/add.js', { items: [{ id: Number(protection.variantId), quantity: 1 }] });
          cart = await fetchCart();
        } else if (!on && covers.length) {
          cart = await request('cart/update.js', { updates: Object.fromEntries(covers.map(line => [line.key, 0])) });
        }
        return settle(cart);
      },
      async restoreGift() {
        session.remove(DECLINED_KEY);
        failedGiftSpend = null;
        return settle(await fetchCart());
      },
      // One product's JSON, 4 requests at a time ("Show all pieces" asks for ~50 at once, and a burst gets 429s); a
      // failed request is tried once more after a pause before the piece is left out.
      product(handle) {
        if (!products.has(handle)) {
          const load = () => queued(() => request(`products/${encodeURIComponent(handle)}.js`));
          products.set(handle, load().catch(() => new Promise(resolve => setTimeout(resolve, 1500)).then(load)).then(normalizeProduct).catch(() => null));
        }
        return products.get(handle);
      },
      suggestions({ productId }) {
        if (!productId) return Promise.resolve([]);
        if (!related.has(productId)) {
          related.set(productId, request(`recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`)
            .then(json => (json?.products || []).map(normalizeProduct).filter(Boolean))
            .catch(() => []));
        }
        return related.get(productId);
      }
    };
  }

  /* ---------- state ---------- */
  let adapter = null;
  let model = null;
  let busy = 0;
  let queue = Promise.resolve();
  let noticeText = '';
  let ready = null;
  const productState = new Map();
  const suggestionState = new Map();
  // What opened the drawer (HumblerUI.openDialog's own opener), so focus can go back to it after the Checkout+ dialog.
  let drawerTrigger = null;
  const getAdapter = () => (adapter ||= window.HumblerCartAdapter || nativeAdapter());
  const checkoutEnabled = () => getAdapter().checkout !== false;
  const roots = () => [...document.querySelectorAll('[data-hb-cart-root]')];

  // Either adapter returns Shopify's cart, where a Checkout+ line is one more line in items, item_count and total_price.
  // The bag shows merchandise: that line leaves items, its quantity and price leave the count and total (so the header
  // count, the set builder and every items.length gate read right), and cart.protection describes it for the Checkout+
  // button (price: the line's own while it is in the bag, else the variant's). Idempotent; a no-op with Checkout+ off.
  function merchandise(cart) {
    if (!cart || cart.protection || !protection.enabled) return cart;
    const covers = (cart.items || []).filter(isProtectionLine);
    const quantity = sum(covers.map(item => item.quantity));
    const linePrice = sum(covers.map(item => item.final_line_price));
    return {
      ...cart,
      items: covers.length ? cart.items.filter(item => !isProtectionLine(item)) : cart.items || [],
      item_count: Math.max(0, cart.item_count - quantity),
      total_price: Math.max(0, cart.total_price - linePrice),
      protection: {
        enabled: true, inCart: covers.length > 0, quantity, keys: covers.map(item => item.key), linePrice,
        price: covers.length ? Math.round(covers[0].final_line_price / Math.max(1, covers[0].quantity)) : protection.price
      }
    };
  }

  function errorText(error) {
    const code = error?.code;
    // Shopify's own message when it speaks to the shopper (stock limits); never its developer wording about parameters.
    if (code === 'shopify' && error.message && error.message !== 'shopify' && !/parameter|\bline\b|\bid\b/i.test(error.message)) return error.message;
    const key = `error_${code}`;
    return code && strings[key] ? t(key, { price: code === 'chains' ? prices.chains : prices.third, pair: prices.pair }) : t('error_generic');
  }

  function run(task, describe) {
    const job = queue.then(async () => {
      setBusy(1);
      try {
        const next = merchandise(await task(getAdapter()));
        commit(next, describe);
        return { ok: true, cart: next };
      } catch (error) {
        const message = errorText(error);
        try { commit(await getAdapter().getCart(), null, message); } catch { showError(message); }
        return { ok: false, error: message, cart: model };
      } finally {
        setBusy(-1);
      }
    });
    queue = job.catch(() => {});
    return job;
  }

  function commit(next, describe, error) {
    next = merchandise(next);
    const previous = model;
    model = next;
    noticeText = error || (next.notice ? errorText(next.notice) : '');
    render();
    const messages = [];
    if (describe) messages.push(describe(next, previous));
    const hadGift = previous?.items.some(item => item.kind === 'gift' && item.final_line_price === 0);
    const hasGift = next.items.some(item => item.kind === 'gift' && item.final_line_price === 0);
    if (previous && !hadGift && hasGift) messages.push(t('status_gift', { title: giftName() }));
    // The bag dropped below the gift threshold and the free gift went with it (a shopper's own removal is
    // already described by the change itself and marks the gift declined).
    else if (hadGift && !hasGift && !next.offers?.gift?.declined) messages.push(t('status_gift_removed', { title: giftName() }));
    if (error) messages.push(error);
    announce(messages.filter(Boolean).join(' '));
    document.dispatchEvent(new CustomEvent('humbler:cart:updated', { detail: { item_count: next.item_count, cart: next } }));
  }

  function setBusy(step) {
    busy = Math.max(0, busy + step);
    for (const root of roots()) {
      root.setAttribute('aria-busy', String(busy > 0));
      renderCheckout(root);
    }
  }

  function showError(message) {
    // The bag never loaded (nothing was changed): say so, and renderNotice offers "Try again".
    noticeText = model ? message : t('load_error');
    roots().forEach(root => {
      // The bag never loaded: drop the first-paint skeletons rather than leave them shimmering, and the drawer's
      // summary card, which has nothing to show (the first render or a retry brings it back).
      if (!model) {
        root.classList.remove('is-pending');
        root.querySelectorAll('[data-hb-skeleton]').forEach(node => node.remove());
        root.querySelectorAll('[data-hb-cart-summary]').forEach(node => { node.hidden = true; });
        // An installments line that only held a bone (the preview bag's) goes too; the server-printed one stays.
        root.querySelectorAll('[data-hb-cart-installments]').forEach(node => { if (!node.textContent.trim()) node.hidden = true; });
      }
      renderNotice(root);
    });
    placeOffers();
  }

  function announce(message) {
    if (!message) return;
    document.querySelectorAll('[data-hb-cart-status]').forEach(region => {
      region.textContent = '';
      setTimeout(() => { region.textContent = message; }, 60);
    });
  }

  /* ---------- rendering ---------- */
  let renderTimer = 0;
  const scheduleRender = () => {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(render, 16);
  };
  let updateBar = () => {};

  function render() {
    if (!model) return;
    for (const root of roots()) renderRoot(root);
    placeOffers();
    updateBar();
    rememberShape();
    fetchFinishes();
  }

  // The rendered bag's shape (set cards, rows, Mystery row, carousel, value rows per surface), kept for this tab
  // so the next page's first-paint skeleton (pendingPlan) matches it exactly while the bag is unchanged.
  // A per-viewer convenience in session storage: without it the skeleton falls back to pendingPlan's rules.
  const SHAPE_KEY = 'humbler:bag-shape';
  const bagSignature = lines => lines.map(line => `${line.key}x${line.quantity}`).join('|');
  function rememberShape() {
    const shape = { sig: bagSignature(model.items.filter(item => !isGiftItem(item))), gift: !!model.offers?.gift.qualifies, installments: !!installmentsText(model.total_price) };
    for (const root of roots()) {
      const list = root.querySelector('[data-hb-cart-lines]');
      if (!list) continue;
      const entries = [...list.children];
      const addon = root.querySelector('[data-hb-cart-addon]');
      const nudges = root.querySelector('[data-hb-cart-nudges]');
      shape[root.dataset.hbCartRoot] = {
        sets: entries.filter(node => node.classList.contains('hb-cart-set')).map(node => node.querySelectorAll('.hb-cart-line').length),
        rows: entries.filter(node => !node.classList.contains('hb-cart-set')).length,
        addon: !!addon?.querySelector('.hb-cart-addon'),
        // The Mystery row's second note line: the unlock hint, or "Ends the $29 3rd-piece price" under what it adds.
        hint: !!addon?.querySelector('.hb-cart-addon__hint, .hb-cart-addon__note ~ .hb-cart-addon__note'),
        nudge: !!nudges?.querySelector('.hb-cart-nudge'),
        kind: nudges?.querySelector('.hb-cart-nudge')?.dataset.kind || '',
        meta: !!nudges?.querySelector('.hb-cart-nudge__meta'),
        values: root.querySelectorAll('[data-hb-cart-value] .hb-cart-value__row').length
      };
    }
    session.set(SHAPE_KEY, JSON.stringify(shape));
  }

  function renderRoot(root) {
    const items = orderedItems();
    const empty = items.length === 0;
    const active = document.activeElement;
    const hadFocus = root.contains(active) && active !== root;
    const focusKey = hadFocus ? active.closest('[data-hb-focus]')?.dataset.hbFocus || '' : null;

    root.dataset.empty = String(empty);
    root.classList.remove('is-pending');
    root.querySelectorAll('[data-hb-cart-filled]').forEach(node => { node.hidden = empty; });
    root.querySelectorAll('[data-hb-cart-empty]').forEach(node => { node.hidden = !empty; });
    root.querySelectorAll('[data-hb-cart-title-count]').forEach(node => {
      node.hidden = empty;
      node.textContent = t('count', { number: model.item_count });
    });
    root.querySelectorAll('[data-hb-cart-items-label]').forEach(node => {
      node.hidden = empty;
      node.textContent = model.item_count === 1 ? strings.items_one || '1' : String(strings.items_other || '').replace(/\d+/, model.item_count);
    });

    renderProgress(root);
    renderNotice(root);
    const list = root.querySelector('[data-hb-cart-lines]');
    if (list) patchList(list, lineEntries(items));
    // The Mystery add-on row sits right under the lines (next to the gift); the offers carousel follows.
    const addon = root.querySelector('[data-hb-cart-addon]');
    const mystery = empty || !model.offers || !cfg ? '' : mysteryCard(model.offers);
    if (addon) patchHtml(addon, mystery);
    const nudges = root.querySelector('[data-hb-cart-nudges]');
    if (nudges) patchHtml(nudges, empty ? '' : (addon ? '' : mystery) + nudgesHtml(items, root));
    renderSummary(root);
    renderCheckout(root);

    // Re-rendered or moved nodes drop focus: put it back on the same control (or its line's next best one).
    if (hadFocus && (document.activeElement !== active || !document.contains(active) || active.disabled || active.closest('[hidden]'))) restoreFocus(root, focusKey);
  }

  // Focus keys are "<line key>:<control>" (line keys can contain ":") or "<nudge>:<variant>".
  function restoreFocus(root, focusKey) {
    const split = focusKey.lastIndexOf(':');
    const key = split > 0 ? focusKey.slice(0, split) : focusKey;
    const isLine = model.items.some(item => item.key === key) || /:(increase|decrease|remove|remove-set|title)$/.test(focusKey);
    const candidates = isLine ? [focusKey, `${key}:remove-set`, `${key}:decrease`, `${key}:remove`, `${key}:increase`, `${key}:title`] : [focusKey];
    for (const candidate of candidates) {
      const node = root.querySelector(`[data-hb-focus="${CSS.escape(candidate)}"]`);
      if (node && !node.disabled && !node.closest('[hidden]')) { node.focus({ preventScroll: true }); return; }
    }
    // A line that left: the first line. A picked card or the Mystery row that left: the next offer control,
    // so keyboard users stay in the offers instead of jumping back to the heading.
    // The Mystery row's Add: the Mystery line it just put in the bag.
    const added = focusKey === 'mystery' ? root.querySelector('[data-hb-cart-lines] [data-kind="mystery"] [data-hb-focus$=":title"]') : null;
    const next = isLine ? root.querySelector('[data-hb-cart-lines] [data-hb-focus$=":title"]')
      : added || [...root.querySelectorAll('[data-hb-cart-addon] [data-hb-focus], [data-hb-cart-nudges] [data-hb-focus]')].find(node => !node.disabled && !node.closest('[hidden]'));
    const heading = root.querySelector('[data-hb-cart-heading], [data-hb-cart-page-heading]');
    (next || heading)?.focus({ preventScroll: true });
  }

  function patchHtml(node, html) {
    if (node._hbHtml === html) return;
    node._hbHtml = html;
    node.innerHTML = html;
  }

  // Keyed list patch: unchanged entries (same key + same markup) keep their DOM (and images).
  function patchList(list, entries) {
    // Unkeyed children (the first-paint skeleton lines) always go.
    [...list.children].forEach(node => { if (!node.dataset.hbKey) node.remove(); });
    const existing = new Map([...list.children].map(node => [node.dataset.hbKey, node]));
    const nodes = entries.map(({ key, html }) => {
      let node = existing.get(key);
      existing.delete(key);
      if (!node || node._hbHtml !== html) {
        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const fresh = template.content.firstElementChild;
        fresh.dataset.hbKey = key;
        fresh._hbHtml = html;
        if (node) node.replaceWith(fresh);
        node = fresh;
      }
      return node;
    });
    existing.forEach(node => node.remove());
    nodes.forEach((node, index) => { if (list.children[index] !== node) list.insertBefore(node, list.children[index] || null); });
  }

  const isGiftItem = item => item.kind === 'gift';
  const isFreeGift = item => isGiftItem(item) && item.final_line_price === 0;
  const isThirdPriced = item => (item.offer_labels || []).includes('third');
  // Paid lines in Shopify order (newest first); the free gift last.
  const orderedItems = () => [...model.items.filter(item => !isGiftItem(item)), ...model.items.filter(isGiftItem)];

  // The currency's smallest unit in Shopify's amounts (which are always ×100): 1 for two-decimal currencies,
  // 100 for zero-decimal ones (JPY, KRW).
  let minorUnit = 0;
  const currencyUnit = () => {
    if (minorUnit) return minorUnit;
    let digits = 2;
    try {
      const code = window.Shopify?.currency?.active || window.HumblerMoney?.currency || 'USD';
      digits = new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits;
    } catch { digits = 2; }
    return (minorUnit = 10 ** Math.max(0, 2 - digits));
  };
  // A set the shopper built (lines sharing a _bundle_id) is priced as that set: every line discounted and together
  // the 2-for price per two units, give or take Shopify's rounding of each line to the currency's smallest unit. In
  // another currency the 2-for price is Shopify's conversion (fromUsd), rounded to that unit; USD stays exact.
  function setPriced(units, paid, lineCount) {
    if (!cfg || units < 2 || units % 2) return false;
    const unit = currencyUnit();
    const pairPrice = Math.round(cfg.pair.price / unit) * unit;
    return Math.abs(paid - (units / 2) * pairPrice) <= lineCount * unit;
  }
  function pricedAsSet(lines) {
    if (!lines.every(line => line.final_line_price < line.original_line_price)) return false;
    return setPriced(sum(lines.map(line => line.quantity)), sum(lines.map(line => line.final_line_price)), lines.length);
  }

  // Set cards, one per set (owner, 2026-10-19). The store's 2-for-$99 function splits its saving set by set (every set
  // the shopper built is a $99 set, each set's 3rd at min(price, $29)) and the engine splits it the same way, so a card
  // is: a set the shopper built (the lines of its _bundle_id, the 3rd added to it among them), another of the engine's
  // sets (its two pieces and the 3rd shown with it), the chain deal, or the pooled extra sets (one saving spread over
  // them). Used while every line costs what the engine says (the preview; Shopify once its discounts price the bag as
  // the engine does); else null, and clusters() groups the lines Shopify discounted. Order: newest first.
  function engineCards(items) {
    const offers = model?.offers;
    if (!cfg || !offers?.lines || !offers.groups) return null;
    const unit = currencyUnit();
    const outOf = new Map(offers.lines.map(line => [line.key, line]));
    if (!items.every(item => outOf.has(item.key) && Math.abs(outOf.get(item.key).finalTotal - item.final_line_price) <= unit)) return null;
    const position = new Map(items.map((item, index) => [item.key, index]));
    const parent = new Map(items.map(item => [item.key, item.key]));
    const find = key => (parent.get(key) === key ? key : find(parent.get(key)));
    const join = keys => {
      const inBag = keys.filter(key => parent.has(key));
      for (const key of inBag.slice(1)) parent.set(find(key), find(inBag[0]));
    };
    // A line is drawn once: it joins the card of every group it is a piece of (a line of 2 can be a piece of two sets:
    // one card), but as a 3rd only its first group's, so one line can't chain two sets together. A built set's card
    // also takes its _bundle_id's lines that are in no group (a leftover piece at its own price).
    const memberships = new Map();
    for (const group of offers.groups) {
      for (const entry of group.units) memberships.set(entry.key, [...(memberships.get(entry.key) || []), { group, role: entry.role }]);
    }
    for (const group of offers.groups) {
      const keys = group.units.filter(entry => {
        if (entry.role !== 'third') return true;
        const of = memberships.get(entry.key);
        return !of.some(member => member.role !== 'third') && of.find(member => member.role === 'third').group === group;
      }).map(entry => entry.key);
      if (group.offer === 'pair' && group.bundle) keys.push(...items.filter(item => item.properties?._bundle_id === group.bundle && !memberships.has(item.key)).map(item => item.key));
      join(keys);
    }
    const cards = new Map();
    for (const item of items) {
      const root = find(item.key);
      if (!cards.has(root)) cards.set(root, Object.assign([], { engine: true }));
      cards.get(root).push(item);
    }
    return [...cards.values()].sort((a, b) => position.get(a[0].key) - position.get(b[0].key));
  }
  // An engine card's name, from what its pieces are: one set ("2 for $99 · Set"), a set and its 3rd ("Set of 3 · 2 for
  // $99 + $29"), several sets, the chain deal, the pooled extra sets (the deal's pieces), else its parts ("Set · 3rd $29
  // + $65": a piece at its own price beside a 3rd).
  function engineLabel(card) {
    const offers = model.offers;
    const keys = new Set(card.map(item => item.key));
    const pieces = sum(card.map(item => item.quantity));
    const groups = offers.groups.filter(group => group.units.some(entry => keys.has(entry.key)));
    const stacks = groups.filter(group => group.offer === 'stack');
    if (stacks.length) {
      if (stacks.length !== groups.length) return t('set_mixed');
      const number = sum(stacks.map(group => group.sets || 1));
      return number === 1 ? t('set_stack', { price: prices.stack }) : t('set_multi', { offer: offerNames().stack, number });
    }
    const chains = groups.some(group => group.offer === 'chains');
    if (chains) {
      if (groups.some(group => group.offer === 'pair')) return t('set_mixed');
      return pieces > (cfg.chains.count || 3) ? t('set_chains_count', { price: prices.chains, number: pieces }) : t('set_chains', { price: prices.chains });
    }
    if (groups.some(group => group.pooled)) return t('set_deal', { price: prices.pair, number: pieces });
    const unitPrice = key => offers.lines.find(line => line.key === key)?.unitPrice || 0;
    const inCard = (group, role) => group.units.filter(entry => entry.role === role && keys.has(entry.key));
    const sets = groups.filter(group => inCard(group, 'pair').length === 2).length;
    const halves = groups.filter(group => inCard(group, 'pair').length === 1).length;
    const thirds = groups.flatMap(group => inCard(group, 'third'));
    const rest = card.flatMap(item => {
      const used = groups.flatMap(group => group.units).filter(entry => entry.key === item.key).length;
      return Array.from({ length: Math.max(0, item.quantity - used) }, () => item.original_price);
    });
    const thirdCost = entry => money(Math.min(unitPrice(entry.key), cfg.third.price));
    if (halves || !sets) return t('set_mixed');
    if (!rest.length && !thirds.length) return sets === 1 ? t('set_pair', { price: prices.pair }) : t('set_multi', { offer: offerNames().pair, number: sets });
    if (!rest.length && sets === 1 && thirds.length === 1) return t('set_third', { pair: prices.pair, price: thirdCost(thirds[0]) });
    if (!rest.length && thirds.length <= sets) return t('set_multi', { offer: offerNames().pair, number: sets });
    const parts = [...Array.from({ length: sets }, () => t('set_part_pair', { price: prices.pair })), ...thirds.map(entry => t('set_part_third', { price: thirdCost(entry) })), ...rest.map(price => money(price))];
    return t('set_parts', { parts: parts.join(' + ') });
  }

  // Set cards when the engine's prices are not Shopify's (e.g. the store's discount has not caught up): a set the
  // shopper built stays one card while it is priced as that set; every other discounted line joins the lines of the
  // offer group that priced it (the engine's grouping of the store's discounts). The two kinds are never chained
  // together, so two sets stay two cards, a card holds only the lines its price covers and "Remove set" removes just
  // that card. Order: newest first.
  function clusters(items) {
    const position = new Map(items.map((item, index) => [item.key, index]));
    const cards = [];
    const taken = new Set();
    const bundles = new Map();
    for (const item of items) {
      const id = item.properties?._bundle_id;
      if (id) bundles.set(id, [...(bundles.get(id) || []), item]);
    }
    for (const lines of bundles.values()) {
      if (lines.length < 2 || !pricedAsSet(lines)) continue;
      cards.push(Object.assign(lines, { bundle: true }));
      lines.forEach(line => taken.add(line.key));
    }
    // Engine groups among the other lines (a line with quantity > 1 can hold units of two groups: one card).
    const rest = items.filter(item => !taken.has(item.key));
    const parent = new Map(rest.map(item => [item.key, item.key]));
    const find = key => (parent.get(key) === key ? key : find(parent.get(key)));
    const owner = new Map();
    for (const item of rest) {
      for (const id of item.offer_groups || []) {
        if (owner.has(id)) parent.set(find(item.key), find(owner.get(id)));
        else owner.set(id, item.key);
      }
    }
    const out = new Map();
    for (const item of rest) {
      const rootKey = find(item.key);
      if (!out.has(rootKey)) out.set(rootKey, []);
      out.get(rootKey).push(item);
    }
    return [...cards, ...out.values()].sort((a, b) => position.get(a[0].key) - position.get(b[0].key));
  }
  // The offers (pair / chains) that priced a card's lines.
  const cardKinds = group => new Set(group.flatMap(item => item.offer_labels || []).filter(label => label === 'pair' || label === 'chains' || label === 'stack'));
  // How many sets a card holds: a built set per two units, else one per engine group in it.
  function cardSets(group, kind) {
    const units = sum(group.filter(item => !isThirdPriced(item)).map(item => item.quantity));
    const per = kind === 'stack' ? 3 : kind === 'chains' ? cfg?.chains.count || 3 : 2;
    if (group.bundle) return Math.max(1, Math.floor(units / (kind === 'stack' ? 3 : 2)));
    const ids = new Set(group.flatMap(item => item.offer_groups || []));
    return Math.max(1, ids.size || Math.floor(units / per));
  }

  // Order: sets → single lines → Mystery add-on line → free gift. The $29 third piece sits inside the set that earns it.
  function lineEntries(items) {
    const paid = items.filter(item => !isGiftItem(item));
    const engine = engineCards(paid);
    const groups = engine || clusters(paid);
    const thirdAt = engine ? -1 : groups.findIndex(group => group.length === 1 && group[0].kind === 'third' && isThirdPriced(group[0]));
    if (thirdAt !== -1) {
      const set = groups.find((group, index) => index !== thirdAt && group.length > 1 && cardKinds(group).has('pair'));
      if (set) set.push(...groups.splice(thirdAt, 1)[0]);
    }
    const single = item => ({ key: `line:${item.key}`, html: `<li class="hb-cart__entry">${lineHtml(item)}</li>` });
    eagerLines = 0;
    return [
      ...groups.filter(group => group.length > 1).map(group => ({ key: `set:${group.map(item => item.key).join('|')}`, html: setHtml(group) })),
      ...groups.filter(group => group.length === 1 && group[0].kind !== 'mystery').map(group => single(group[0])),
      ...groups.filter(group => group.length === 1 && group[0].kind === 'mystery').map(group => single(group[0])),
      ...items.filter(isGiftItem).map(single)
    ];
  }

  // One set card (grey card): a two-line head — offer name + set price (struck regular total), then the saving +
  // "Remove set" — over its piece lines. All prices are the lines' own (Shopify's on the live store).
  function setHtml(group) {
    const kinds = cardKinds(group);
    const kind = kinds.size === 1 ? [...kinds][0] : '';
    const third = group.some(isThirdPriced);
    const sets = kind ? cardSets(group, kind) : 1;
    const pieces = sum(group.map(item => item.quantity));
    const now = sum(group.map(item => item.final_line_price));
    const regular = sum(group.map(item => item.original_line_price));
    // A set of 3 names what its 3rd really costs: $29, or a cheaper piece's own price ($99 + $27 for a $27 bracelet).
    const thirdCost = cfg && now > cfg.pair.price && now - cfg.pair.price < cfg.third.price ? money(now - cfg.pair.price) : prices.third;
    // Named from what is in the card: one pair (+ the 3rd), the deal's 3 or more pieces, one chain set, or several sets.
    const label = group.engine ? engineLabel(group)
      : !kind ? t('set_mixed')
      : kind === 'stack' && sets > 1 ? t('set_multi', { offer: offerNames().stack, number: sets })
      : kind === 'stack' ? t('set_stack', { price: prices.stack })
      : kind === 'pair' && pieces >= 3 && !(third && pieces === 3) ? t('set_deal', { price: prices.pair, number: pieces })
      : sets > 1 ? t('set_multi', { offer: offerNames()[kind], number: sets })
      : kind === 'pair' && third ? t('set_third', { pair: prices.pair, price: thirdCost })
      : kind === 'chains' && pieces > (cfg?.chains.count || 3) ? t('set_chains_count', { price: prices.chains, number: pieces })
      : kind === 'chains' ? t('set_chains', { price: prices.chains }) : t('set_pair', { price: prices.pair });
    const saving = Math.max(0, regular - now);
    const keys = group.map(item => item.key);
    const titles = group.map(spokenTitle).join(', ');
    return `<li class="hb-cart-set">
      <div class="hb-cart-set__head">
        <p class="hb-cart-set__label">${esc(label)}</p>
        <p class="hb-cart-set__price"><span class="hb-sr">${esc(t('set_price'))}</span><span class="hb-cart-set__now">${esc(money(now))}</span>${saving > 0 ? `<s class="hb-cart-set__was"><span class="hb-sr">${esc(t('regular_price'))}</span>${esc(money(regular))}</s>` : ''}</p>
        <p class="hb-cart-set__saving">${saving > 0 ? esc(t('set_saving', { amount: money(saving) })) : ''}</p>
        <button type="button" class="hb-cart-set__remove" data-hb-cart-action="remove-set" data-keys="${esc(JSON.stringify(keys))}" data-hb-focus="${esc(keys[0])}:remove-set" aria-label="${esc(t('remove_set_label', { title: titles }))}">${esc(t('remove_set'))}</button>
      </div>
      <ul class="hb-cart-set__lines" role="list" aria-label="${esc(label)}">${group.map(item => `<li>${lineHtml(item, true)}</li>`).join('')}</ul>
    </li>`;
  }

  const finishWord = text => ['black', 'gold', 'silver'].find(word => String(text || '').toLowerCase().split(/[^a-z]+/).includes(word)) || '';
  // A piece sold in several metals (the custom dog tag's "Pendant": Gold / Silver / Black) takes its line's own
  // variant, as snippets/humbler-finish does (never the store's gold/silver collections, which list such a piece
  // under one metal): a finish/colour/metal/"Pendant" option naming a metal, or an option value that is one.
  // null when the variant does not say.
  function variantFinish(item) {
    const metal = value => ['gold', 'silver', 'black'].find(word => String(value ?? '').toLowerCase().includes(word)) || '';
    const options = (Array.isArray(item.options_with_values) ? item.options_with_values : []).filter(Boolean);
    const named = options.find(option => /^(pendant|finish|colou?r|metal)$/i.test(String(option.name ?? '').trim()));
    if (named && (metal(named.value) || /^pendant$/i.test(String(named.name).trim()))) return metal(named.value);
    const own = [...options.map(option => option.value), item.variant_title].find(value => /^(gold|silver|black)$/i.test(String(value ?? '').trim()));
    return own ? String(own).trim().toLowerCase() : null;
  }
  // Each line's finish, in snippets/humbler-finish's order: the server's answer for the bag's variants (the drawer
  // prints it, and reprints it for lines added on this page: fetchFinishes), the line's own variant for a piece sold
  // in several metals, "black" in the handle, the store's gold/silver collections (the preview catalog has them),
  // then the handle or variant title.
  const finishes = new Map(Object.entries(settingsData.finishes || {}).map(([id, finish]) => [String(id), String(finish || '')]));
  function finishKey(item) {
    const known = finishes.get(String(item.variant_id ?? ''));
    if (known) return known;
    const own = variantFinish(item);
    if (own !== null) return own;
    const word = finishWord(`${item.handle || ''} ${item.variant_title || ''}`);
    if (word === 'black') return word;
    const product = getAdapter().name === 'preview' && item.handle ? productData(item.handle).product : null;
    const collection = (product?.collections || []).map(handle => String(handle).split('-')[0]).find(prefix => prefix === 'gold' || prefix === 'silver');
    return collection || word;
  }
  function finishOf(item) {
    const finish = finishKey(item);
    return finish ? strings[`finish_${finish}`] || finish : '';
  }
  // A line as the live status and its controls name it: the display title, never the raw (often all-caps)
  // catalog title, plus its finish and a real variant, so gold and silver twins (and lengths) stay apart:
  // "3mm Cuban Link Chain (Gold, 20")". The Mystery line uses its row title, the gift its "Free …" name.
  function spokenTitle(item) {
    if (!item) return '';
    if (item.kind === 'mystery') return t('mystery_title');
    const name = displayTitle(item.product_title || item.title || '');
    if (isGiftItem(item)) return t('gift_title', { title: name });
    const engraving = engravingOf(item.properties);
    const details = [finishOf(item), variantOf(item), engraving ? t('engraving_line', { value: engraving }) : ''].filter(Boolean);
    return details.length ? `${name} (${details.join(', ')})` : name;
  }
  // Shopify: a line added on this page whose finish the handle does not tell: ask the drawer section again
  // (Section Rendering API, a storefront GET) for its finish map, once per variant.
  let finishFetch = null;
  const finishAsked = new Set();
  function fetchFinishes() {
    if (!model || finishFetch || !settingsData.sectionId || getAdapter().name !== 'native') return;
    const missing = model.items.filter(item => !isGiftItem(item) && item.kind !== 'mystery' && !finishes.has(String(item.variant_id)) &&
      !finishAsked.has(String(item.variant_id)) && !finishWord(`${item.handle || ''} ${item.variant_title || ''}`));
    if (!missing.length) return;
    missing.forEach(item => finishAsked.add(String(item.variant_id)));
    finishFetch = fetch(`${route('')}?sections=${encodeURIComponent(settingsData.sectionId)}`, { headers: { Accept: 'application/json' } })
      .then(response => (response.ok ? response.json() : null))
      .then(json => {
        const template = document.createElement('template');
        template.innerHTML = json?.[settingsData.sectionId] || '';
        const data = JSON.parse(template.content.querySelector('[data-hb-cart-settings]')?.textContent || '{}');
        Object.entries(data.finishes || {}).forEach(([id, finish]) => finishes.set(String(id), String(finish || '')));
      })
      .catch(() => {})
      .finally(() => { finishFetch = null; scheduleRender(); });
  }
  function variantOf(item) {
    const title = item.variant_title;
    if (!title || title === 'Default Title' || /^(gold|silver|black)$/i.test(title.trim())) return '';
    return title;
  }
  const offerNames = () => ({ pair: t('pill_pair', { price: prices.pair }), chains: t('pill_chains', { price: prices.chains }), stack: t('pill_stack', { price: prices.stack }), third: t('pill_third', { price: prices.third }), gift: t('pill_gift') });
  function labelsOf(item) {
    const names = offerNames();
    const labels = (item.offer_labels || []).map(label => names[label]).filter(Boolean);
    if (!labels.length && item.final_line_price < item.original_line_price) {
      for (const allocation of item.line_level_discount_allocations || []) {
        const title = allocation.discount_application?.title;
        if (title && !labels.includes(title)) labels.push(title);
      }
    }
    return labels;
  }
  // Lazy images fade in over a shimmering stage (assets/humbler-skeleton.js); the first bag lines load
  // eagerly so they are already there when the drawer opens.
  const pictureHtml = (src, width, sizes, eager = false) => (src
    ? `<img src="${esc(imageUrl(src, width * 2))}" srcset="${esc(imageUrl(src, width * 2))} ${width * 2}w, ${esc(imageUrl(src, width * 3))} ${width * 3}w" sizes="${sizes}" width="${width}" height="${Math.round(width * 1.25)}" alt="" loading="${eager ? 'eager' : 'lazy'}">`
    : '');
  const EAGER_LINES = 6;
  let eagerLines = 0;
  // Bag photos: the 4:5 packshot on the #F9F9F9 stage (96px wide, 88px in a set; 108px on /cart from 700px).
  const thumbHtml = (src, eager = false) => (src
    ? `<img src="${esc(imageUrl(src, 216))}" srcset="${esc(imageUrl(src, 216))} 216w, ${esc(imageUrl(src, 324))} 324w" sizes="(min-width: 700px) 108px, 96px" width="108" height="135" alt="" loading="${eager ? 'eager' : 'lazy'}">`
    : '');

  // One line card (JAXXON's cart line): 4:5 photo | the name (15px, wraps) with a remove button beside it for lines
  // without a stepper (set pieces, the gift, a $29 third piece), a 13px detail line (category · finish · size ·
  // offer), then a foot: the stepper (trash in place of − at quantity 1) and the price, the struck regular price
  // before it. Lines inside a set skip the set's own offer names: the set head already says them (the $29 piece
  // shows its price).
  // A line with an engraving ('text-box-1') gets one more, wrapping line under the detail line: Engraving "…" in full
  // (.hb-cart-line--prop; snippets/humbler-cart-skeleton prints the same line on first paint).
  function lineHtml(item, inSet = false) {
    const isGift = isGiftItem(item);
    const fixed = isGift;
    const stepper = !inSet && !fixed;
    const key = esc(item.key);
    const fullTitle = item.product_title || item.title || '';
    const isMystery = item.kind === 'mystery';
    const parts = splitTitle(fullTitle);
    const title = isGift ? displayCase(fullTitle) : isMystery ? t('mystery_title') : parts.name;
    const url = esc(item.url || '#');
    const variant = variantOf(item);
    const finish = isMystery ? '' : finishOf(item);
    const spoken = spokenTitle(item);
    const meta = isMystery
      ? [Number(raw.mystery?.compareAtPrice) > Number(item.final_price ?? item.price ?? 0) ? t('mystery_line_meta', { value: money(Number(raw.mystery.compareAtPrice)) }) : t('mystery_eyebrow')]
      : [isGift ? '' : parts.category, finish, variant];
    if (!stepper && item.quantity > 1) meta.push(t('qty', { quantity: item.quantity }));
    const names = offerNames();
    const setNames = [names.pair, names.chains, names.third];
    const offer = isGift ? names.gift : labelsOf(item).filter(label => !inSet || !setNames.includes(label)).join(' · ');
    // A piece in a set card shows its own price: the card's head carries the deal (owner, 2026-10-18: never a per-piece
    // deal price such as "$42.66"). Elsewhere a discounted line shows its final price over its regular one.
    const shown = inSet && !isGift ? item.original_line_price : item.final_line_price;
    const discounted = !inSet && item.final_line_price < item.original_line_price;
    const free = isFreeGift(item);
    const max = Number(item.quantity_rule?.max) || Infinity;
    const first = item.quantity <= 1
      ? `<button type="button" class="hb-qty__button" data-hb-cart-action="remove" data-key="${key}" data-hb-focus="${key}:remove" aria-label="${esc(t('remove', { title: spoken }))}">${icon('trash')}</button>`
      : `<button type="button" class="hb-qty__button" data-hb-cart-action="decrease" data-key="${key}" data-hb-focus="${key}:decrease" aria-label="${esc(t('decrease', { title: spoken }))}">${icon('minus')}</button>`;
    const quantity = stepper
      ? `<div class="hb-qty" role="group" aria-label="${esc(t('quantity_label', { title: spoken }))}">${first}<span class="hb-qty__value">${item.quantity}</span><button type="button" class="hb-qty__button" data-hb-cart-action="increase" data-key="${key}" data-hb-focus="${key}:increase" aria-label="${esc(t('increase', { title: spoken }))}"${item.quantity >= max ? ' disabled' : ''}>${icon('plus')}</button></div>`
      : '';
    const remove = stepper ? ''
      : `<button type="button" class="hb-cart-line__remove" data-hb-cart-action="remove" data-key="${key}" data-hb-focus="${key}:remove" aria-label="${esc(isGift ? t('remove_gift') : t('remove', { title: spoken }))}">${icon('trash')}</button>`;
    const image = isGift && settingsData.giftImage ? settingsData.giftImage : item.image;
    const metaHtml = [...meta.filter(Boolean).map(esc), offer ? `<span class="hb-cart-line__offer">${esc(offer)}</span>` : ''].filter(Boolean).join(' · ');
    const now = free ? `<span class="hb-cart-line__free">${esc(t('free'))}</span>` : `<span class="hb-cart-line__now">${esc(money(shown))}</span>`;
    const engraving = engravingOf(item.properties);
    return `<article class="hb-cart-line${inSet ? ' hb-cart-line--piece' : ''}${stepper ? ' hb-cart-line--qty' : ''}${engraving ? ' hb-cart-line--prop' : ''}${isGift ? ' hb-cart-line--gift' : ''}${isMystery ? ' hb-cart-line--mystery' : ''}${fixed ? ' hb-cart-line--fixed' : ''}" data-kind="${esc(item.kind)}" data-key="${key}">
      <a class="hb-cart-line__media hb-stage" href="${url}" tabindex="-1" aria-hidden="true">${thumbHtml(image, eagerLines++ < EAGER_LINES)}</a>
      <div class="hb-cart-line__main">
        <div class="hb-cart-line__head">
          <h3 class="hb-cart-line__title"><a href="${url}" data-hb-focus="${key}:title" aria-label="${esc(isMystery ? spoken : [displayTitle(fullTitle), finish, variant, engraving ? t('engraving_line', { value: engraving }) : ''].filter(Boolean).join(', '))}">${esc(title)}</a></h3>
        </div>
        ${metaHtml ? `<p class="hb-cart-line__meta">${metaHtml}</p>` : ''}
        ${engraving ? `<p class="hb-cart-line__prop">${esc(t('engraving_line', { value: engraving }))}</p>` : ''}
        <div class="hb-cart-line__foot">
          ${quantity}${remove}
          <p class="hb-cart-line__price"><span class="hb-sr">${esc(t('price'))}</span>${now}${discounted ? `<s class="hb-cart-line__was"><span class="hb-sr">${esc(t('regular_price'))}</span>${esc(money(item.original_line_price))}</s>` : ''}</p>
        </div>
      </div>
    </article>`;
  }

  /* ---------- progress strip: one bar, shipping + gift milestones (equal segments) ---------- */
  // The strip is about the free gift only (owner, 2026-10-17: no free-shipping step; the summary card says Shipping).
  function progressCopy(offers) {
    const hint = offers.gift;
    const short = giftName();
    if (hint.qualifies) return t('progress_gift_unlocked', { title: short });
    // One line from 320px: the short name, its value in front of it ("$50 away from a free $65 cross pendant").
    return t('progress_gift', { title: short, amount: money(hint.remaining), value: money(gift.value) });
  }

  function renderProgress(root) {
    const slot = root.querySelector('[data-hb-cart-progress-slot]');
    if (!slot) return;
    const offers = model.offers;
    if (!offers || !model.items.length || !gift.enabled) { slot.hidden = true; return; }
    slot.hidden = false;
    // The server's skeleton strip (snippets/humbler-cart-skeleton) is replaced by the real one.
    let strip = slot.querySelector(':scope > .hb-cart-progress:not([data-hb-skeleton])');
    if (!strip) {
      const giftImage = settingsData.giftImage || raw.gift?.image;
      slot.innerHTML = `<div class="hb-cart-progress">
        <div class="hb-cart-progress__row">
          ${giftImage ? `<span class="hb-cart-progress__thumb hb-stage" aria-hidden="true"><img src="${esc(imageUrl(giftImage, 96))}" width="48" height="48" alt=""></span>` : ''}
          <p class="hb-cart-progress__text" data-hb-progress-text></p>
          <button type="button" class="hb-cart-progress__restore" data-hb-cart-action="restore-gift" data-hb-focus="restore-gift" hidden>${icon('gift')}<span>${esc(t('restore_gift'))}</span></button>
        </div>
        <div class="hb-cart-progress__track" role="progressbar" aria-label="${esc(t('progress_label'))}" aria-valuemin="0" aria-valuemax="100" data-hb-progress-track>
          <span class="hb-cart-progress__fill" data-hb-progress-fill></span>
          <span data-hb-progress-ticks></span>
        </div>
        <ol class="hb-cart-progress__milestones" role="list" data-hb-progress-milestones></ol>
      </div>`;
      strip = slot.firstElementChild;
    }
    const milestones = [{ type: 'gift', threshold: offers.gift.threshold, reached: offers.gift.qualifies }];
    const value = offers.gift.spend;
    let fraction = 1;
    let floor = 0;
    let placed = false;
    for (const [index, milestone] of milestones.entries()) {
      const start = index / milestones.length;
      const end = (index + 1) / milestones.length;
      milestone.at = end;
      if (!placed && value < milestone.threshold) {
        fraction = start + ((end - start) * Math.max(0, value - floor)) / Math.max(1, milestone.threshold - floor);
        placed = true;
      }
      floor = milestone.threshold;
    }
    for (const milestone of milestones) if (milestone.reached) fraction = Math.max(fraction, milestone.at);
    const copy = progressCopy(offers);
    strip.classList.toggle('is-done', !!offers.gift.qualifies);
    strip.querySelector('[data-hb-progress-text]').innerHTML = (offers.gift.qualifies ? icon('check-xs') : '') + `<span>${esc(copy)}</span>`;
    strip.querySelector('[data-hb-progress-fill]').style.setProperty('--hb-progress', fraction.toFixed(4));
    const track = strip.querySelector('[data-hb-progress-track]');
    track.setAttribute('aria-valuenow', String(Math.round(fraction * 100)));
    track.setAttribute('aria-valuetext', copy);
    patchHtml(strip.querySelector('[data-hb-progress-ticks]'), milestones.slice(0, -1).map(milestone => `<i class="hb-cart-progress__tick${milestone.reached ? ' is-reached' : ''}" style="--at: ${(milestone.at * 100).toFixed(2)}%"></i>`).join(''));
    patchHtml(strip.querySelector('[data-hb-progress-milestones]'), milestones.map(milestone => {
      const label = t('milestone_gift', { amount: money(milestone.threshold) });
      return `<li class="hb-cart-progress__milestone${milestone.reached ? ' is-reached' : ''}" style="--at: ${(milestone.at * 100).toFixed(2)}%">${milestone.reached ? icon('check-xs') : ''}<span>${esc(label)}</span></li>`;
    }).join(''));
    const hint = offers.gift;
    strip.querySelector('[data-hb-cart-action="restore-gift"]').hidden = !(gift.enabled && hint.qualifies && hint.declined && !hint.inCart && !hint.owned);
  }

  function renderNotice(root) {
    const retry = !model && noticeText ? t('load_retry') : '';
    root.querySelectorAll('[data-hb-cart-notice]').forEach(node => {
      node.hidden = !noticeText;
      const key = `${noticeText}\n${retry}`;
      if (node._hbNotice === key) return;
      node._hbNotice = key;
      node.textContent = noticeText;
      if (!retry) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hb-cart__retry';
      button.setAttribute('data-hb-cart-retry', '');
      button.textContent = retry;
      node.append(' ', button);
    });
  }
  // First load failed: show the skeletons again and reload the bag.
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-hb-cart-retry]');
    if (!button) return;
    const root = button.closest('[data-hb-cart-root]');
    noticeText = '';
    roots().forEach(renderNotice);
    renderPending();
    root?.querySelector('[data-hb-cart-heading], [data-hb-cart-page-heading]')?.focus({ preventScroll: true });
    window.HumblerCart?.refresh();
  });

  /* ---------- value summary + totals ---------- */
  // Built from line prices (Shopify's own on the live store): subtotal at regular price, one row per offer,
  // the free gift with its value, shipping. Cart-level discounts (codes) get their own row.
  function summary() {
    const gifts = model.items.filter(isFreeGift);
    const paid = model.items.filter(item => !isFreeGift(item));
    const subtotal = sum(paid.map(item => item.original_line_price));
    const total = model.total_price;
    const names = offerNames();
    const rank = { [names.pair]: 0, [names.chains]: 1, [names.third]: 2 };
    const offers = new Map();
    for (const item of paid) {
      const amount = item.original_line_price - item.final_line_price;
      if (amount <= 0) continue;
      let name = labelsOf(item).find(label => label !== names.gift) || t('discount');
      // The 2-for-$99 deal is one saving at checkout, spread over all its lines (the 3rd's line among them): one total.
      if (name === names.third) name = names.pair;
      offers.set(name, (offers.get(name) || 0) + amount);
    }
    // Shown as the offer reads: what the $99 sets save and what their $29 3rds save over their own price (the engine's
    // split, over all its sets), when that adds up to Shopify's own discount; else the one deal row as it is.
    const deals = (model.offers?.groups || []).filter(group => group.offer === 'pair');
    const setSavings = sum(deals.map(group => group.setSavings || 0));
    const thirdSavings = sum(deals.map(group => group.thirdSavings || 0));
    if (deals.length && thirdSavings > 0 && setSavings > 0 && offers.get(names.pair) === sum(deals.map(group => group.savings))) {
      offers.set(names.pair, setSavings);
      offers.set(names.third, thirdSavings);
    }
    const cartLevel = sum(paid.map(item => item.final_line_price)) - total;
    if (cartLevel > 0) {
      const name = (model.cart_level_discount_applications || []).map(entry => entry.title).filter(Boolean).join(', ') || t('discount');
      offers.set(name, (offers.get(name) || 0) + cartLevel);
    }
    const rows = [...offers.entries()].sort((a, b) => (rank[a[0]] ?? 9) - (rank[b[0]] ?? 9));
    return { subtotal, total, savings: Math.max(0, subtotal - total), rows, gifts };
  }

  function valueHtml(data) {
    const row = (label, value, extra = '') => `<div class="hb-cart-value__row${extra}"><dt>${esc(label)}</dt><dd>${value}</dd></div>`;
    const shipping = model.offers?.shipping;
    return `<dl class="hb-cart-value">
      ${row(t('subtotal'), esc(money(data.subtotal)))}
      ${data.rows.map(([label, amount]) => row(label, `−${esc(money(amount))}`, ' is-saving')).join('')}
      ${data.gifts.map(item => row(displayTitle(item.product_title), esc(t('gift_free_value', { amount: money(item.original_line_price) })))).join('')}
      ${shipping ? row(t('shipping'), shipping.qualifies ? `<span class="hb-cart-value__free">${icon('check-xs')}${esc(t('shipping_free'))}</span>` : `<span class="hb-cart-value__note">${esc(t('shipping_calculated'))}</span>`) : ''}
    </dl>`;
  }

  function installmentsText(total) {
    // Shop Pay Installments are US dollars only (thresholds below are USD cents).
    if (!settingsData.installments || localized()) return '';
    if (total >= 5000) return t('installments_4', { amount: money(Math.round(total / 4)) });
    if (settingsData.payIn2 && total >= 3500) return t('installments_2', { amount: money(Math.round(total / 2)) });
    return '';
  }

  function renderSummary(root) {
    const data = summary();
    const set = (selector, text) => root.querySelectorAll(selector).forEach(node => { if (node.textContent !== text) node.textContent = text; });
    set('[data-hb-cart-total]', money(data.total));
    // Checkout+: its own price nodes (every [data-hb-cart-total] is the plain total), so the fee shows before the tap.
    if (model.protection) {
      set('[data-hb-checkout-plus-total]', money(data.total + model.protection.price));
      set('[data-hb-checkout-plus-price]', money(model.protection.price));
    }
    set('[data-hb-cart-save]', t('you_save', { amount: money(data.savings) }));
    root.querySelectorAll('[data-hb-cart-save]').forEach(node => { node.hidden = data.savings <= 0; });
    const installments = installmentsText(data.total);
    set('[data-hb-cart-installments]', installments);
    root.querySelectorAll('[data-hb-cart-installments]').forEach(node => { node.hidden = !installments; });
    root.querySelectorAll('[data-hb-cart-value]').forEach(node => patchHtml(node, model.items.length ? valueHtml(data) : ''));
  }

  // The preview keeps Checkout in its normal ink style; a click explains it is off (see the submit handler).
  function renderCheckout(root) {
    root.querySelectorAll('[data-hb-cart-checkout]').forEach(button => {
      button.disabled = !model?.items.length;
      button.classList.toggle('is-loading', busy > 0);
    });
  }

  /* ---------- offers: one carousel (third piece > add one more > complete your set), plus the mystery add-on ---------- */
  function productData(handle) {
    let entry = productState.get(handle);
    if (!entry) {
      entry = { loading: true, product: null };
      productState.set(handle, entry);
      Promise.resolve(getAdapter().product(handle)).then(product => { entry.product = normalizeProduct(product); }, () => {}).finally(() => { entry.loading = false; scheduleRender(); });
    }
    return entry;
  }

  function suggestionData(item) {
    let entry = suggestionState.get(item.handle);
    if (!entry) {
      entry = { loading: true, products: [] };
      suggestionState.set(item.handle, entry);
      Promise.resolve(getAdapter().suggestions({ productId: item.product_id, handle: item.handle }))
        .then(list => { entry.products = (list || []).map(normalizeProduct).filter(Boolean); }, () => {})
        .finally(() => { entry.loading = false; scheduleRender(); });
    }
    return entry;
  }

  // Each finish is its own product: show one card per design, in the finish already in the bag when possible.
  function uniqueDesigns(picks, finish) {
    const byDesign = new Map();
    for (const pick of picks) {
      const design = designOf(pick.product.title);
      const current = byDesign.get(design);
      if (!current) byDesign.set(design, pick);
      else if (finish && finishWord(current.product.handle) !== finish && finishWord(pick.product.handle) === finish) byDesign.set(design, pick);
    }
    return [...byDesign.values()];
  }
  const bagDesigns = items => new Set(items.filter(item => !isGiftItem(item)).map(item => designOf(item.product_title)));

  // A pick adds in one tap only when it is a plain piece (personalKind); an engraving or builder piece, or one the
  // page cannot classify, links to its page ("Personalise …" for the first two, "Choose options" otherwise).
  // JAXXON's "Before you go" card: 4:5 photo with a white round "+" on it, the name (two lines at most), then the
  // struck regular price before the price, in sale red when it is lower (assets/humbler-cart.css orders them).
  // The control's word (Add / Choose / Personalise) shows only in the Ridge bag, where the control is a full-width
  // outlined button under the price; the aria-label names it either way. It comes last, so keyboard focus meets the
  // name before its Add (Classic places it over the photo's corner).
  function pickHtml({ product, variant, price, compare, action, label, focus, kind = 'plain', sizes = null, bundle = '' }) {
    const url = esc(product.url);
    // A 3rd piece with sizes or lengths: a menu of the ones in stock above its Add, which adds the one chosen.
    const option = (product.options || []).filter(name => name !== 'Title').join(' / ') || t('size');
    const menu = sizes && sizes.length > 1
      ? `<select class="hb-cart-pick__size" data-hb-pick-size data-handle="${esc(product.handle)}" data-hb-focus="third-size:${esc(product.handle)}" aria-label="${esc(t('pick_option', { value: option, title: product.title }))}">${sizes.map(entry => `<option value="${esc(entry.id)}"${String(entry.id) === String(variant.id) ? ' selected' : ''}>${esc(String(entry.title).replace(/"/g, '″').replace(/\(\s*(.*?)\s*\)/, '($1)'))}</option>`).join('')}</select>`
      : '';
    const personalised = kind === 'engraving' || kind === 'builder';
    const control = action && kind === 'plain'
      ? `<button type="button" class="hb-cart-pick__add" data-hb-cart-action="${action}" data-variant-id="${esc(variant.id)}" data-product-id="${esc(product.id)}" data-handle="${esc(product.handle)}"${bundle ? ` data-bundle="${esc(bundle)}"` : ''} data-hb-kind="plain" data-hb-focus="${esc(focus)}" aria-label="${esc(label)}">${icon('plus')}<span class="hb-cart-pick__label">${esc(t('add'))}</span></button>`
      : `<a class="hb-cart-pick__add" href="${url}" data-hb-focus="${esc(focus)}" aria-label="${esc(t(personalised ? 'personalise_label' : 'choose_label', { title: product.title }))}">${icon('arrow')}<span class="hb-cart-pick__label">${esc(t(personalised ? 'pick_personalise' : 'choose'))}</span></a>`;
    return `<li class="hb-cart-pick">
      <div class="hb-cart-pick__visual">
        <a class="hb-cart-pick__media hb-stage" href="${url}" tabindex="-1" aria-hidden="true">${pictureHtml(variant.image || product.image, 168, '(min-width: 700px) 168px, 136px')}</a>
      </div>
      <a class="hb-cart-pick__title" href="${url}" aria-label="${esc(product.title)}">${esc(compactTitle(product.title))}</a>
      <p class="hb-cart-pick__price"><span${compare > price ? ' class="hb-cart-pick__sale"' : ''}>${esc(money(price))}</span>${compare > price ? `<s><span class="hb-sr">${esc(t('regular_price'))}</span>${esc(money(compare))}</s>` : ''}</p>
      ${menu}${control}
    </li>`;
  }
  // Same box as pickHtml (image, title line, price line), so the row keeps its height when picks arrive.
  const skeletonPicks = count => Array.from({ length: count }, (_, index) => `<li class="hb-cart-pick hb-cart-pick--loading" aria-hidden="true">
      <div class="hb-cart-pick__visual"><span class="hb-cart-pick__media hb-skeleton"></span></div>
      <span class="hb-cart-pick__title"><span class="hb-skeleton hb-skeleton--text" style="--w: ${[82, 68, 76][index % 3]}%"></span></span>
      <span class="hb-cart-pick__price"><span class="hb-skeleton hb-skeleton--text" style="--w: ${[40, 48, 36][index % 3]}%"></span></span>
    </li>`).join('');

  let thirdAll = false; // "Show all" pressed (Ridge bag): every third-piece pick from then on
  const pickSizes = new Map(); // product handle → the variant chosen in a 3rd-piece pick's size menu
  // A 3rd piece the store prices by its product (any 2-for-$99 piece): the size chosen, else 22″ (the house length),
  // else the first in stock.
  const thirdVariant = product => {
    const stock = (product?.variants || []).filter(variant => variant.available);
    return stock.find(variant => String(variant.id) === pickSizes.get(product.handle))
      || stock.find(variant => /^22\s*["″”]/.test(String(variant.title))) || stock[0] || null;
  };
  // What a piece adds to the bag as the 3rd, to the cent as checkout prices it ($29 after a $99 set; a $27 bracelet $27),
  // on the pieces themselves: a cross pendant the shopper bought that it makes free is the gift, not part of its price.
  // `before`: the bag's own pieces' engine total (merchandiseTotal), worked out once per card.
  const merchandiseTotal = result => sum(result.lines.filter(line => line.kind !== 'gift' && line.handle !== cfg.gift.handle).map(line => line.finalTotal));
  // `bundle`: the _bundle_id of the set the 3rd joins (the add gives it that set's _bundle_id).
  function thirdCost(product, variant, lines, before, bundle = '') {
    return merchandiseTotal(window.HumblerOffers.price([...lines, { key: '\u0000third', handle: product.handle, variantId: String(variant.id), productId: product.id, price: variant.price, quantity: 1, kind: 'third', bundle }], cfg)) - before;
  }
  // The set a 3rd from the bag joins (each set takes its own $29 3rd). { bundle, names }: the newest set the shopper
  // built that has no 3rd (its 2 pieces only), and its _bundle_id, which the 3rd gets; else the first set with no 3rd
  // in the engine's order, where the store puts a 3rd built into no set ('' bundle). names: that set's pieces.
  function thirdTarget(offers, items) {
    const position = new Map(items.map((item, index) => [item.key, index]));
    const open = (offers.groups || []).filter(group => group.offer === 'pair' && !group.pooled && !group.units.some(entry => entry.role === 'third'));
    const first = group => Math.min(...group.units.map(entry => position.get(entry.key) ?? Infinity));
    const bundleUnits = bundle => sum(items.filter(item => item.properties?._bundle_id === bundle).map(item => item.quantity));
    const built = open.filter(group => group.bundle && bundleUnits(group.bundle) === 2).sort((a, b) => first(a) - first(b))[0];
    const target = built || open[0];
    if (!target) return { bundle: '', names: '' };
    const names = [...new Set(target.units.map(entry => items.find(item => item.key === entry.key)).filter(Boolean).map(item => splitTitle(item.product_title || item.title || '').name))];
    return { bundle: built ? built.bundle : '', names: names.join(' + ') };
  }
  function thirdNudge(offers, items) {
    const owned = bagDesigns(items);
    // Any 2-for-$99 piece (third.any) makes a long list: the suggested pieces (the listed variants) load first, the
    // rest with "Show all pieces" (the first 8 when nothing is listed).
    const any = !!cfg.third.any;
    const listed = offers.third.choices.filter(choice => choice.variantId);
    const choices = any && !thirdAll ? (listed.length ? listed : offers.third.choices.slice(0, 8)) : offers.third.choices;
    const hidden = offers.third.choices.length > choices.length;
    const entries = choices.map(choice => ({ choice, data: productData(choice.handle) }));
    const config = engravingConfig(document);
    const lines = engineLines(items);
    const before = merchandiseTotal(window.HumblerOffers.price(lines, cfg));
    const target = thirdTarget(offers, items);
    // Every set can take its own 3rd. When a 3rd also unlocks the free gift the card says so ("Unlocks your free …")
    // and lists only the pieces whose real rise takes the bag there (a 3rd chain after two chains makes the $99 chain
    // deal: it adds nothing and is left out); when none does, it lists them all without the promise.
    const reaches = cost => offers.gift.spend + cost >= offers.gift.threshold;
    const candidates = entries.map(({ choice, data }) => {
      const product = data.product;
      const variant = choice.variantId ? product?.variants.find(entry => String(entry.id) === String(choice.variantId)) : thirdVariant(product);
      const kind = personalKind(product, config);
      const sizes = choice.variantId ? null : product?.variants.filter(entry => entry.available);
      if (!variant?.available || owned.has(designOf(product.title)) || kind === 'hidden' || kind === 'engraving' || kind === 'builder') return null;
      const cost = thirdCost(product, variant, lines, before, target.bundle);
      // Only a pick the set's slot prices as its 3rd: min(price, $29). (A chain while the chain deal is on joins that
      // deal at $33 instead; it is no "$29" 3rd.)
      return cost > 0 && cost <= Math.min(variant.price, cfg.third.price) ? { product, variant, sizes, cost } : null;
    }).filter(Boolean);
    const loading = entries.some(entry => entry.data.loading);
    const reaching = candidates.filter(({ cost }) => reaches(cost));
    // While picks load, the promise is the engine's (so the line does not drop out and come back).
    const promise = gift.enabled && !!offers.third.unlocksGift && (loading || reaching.length > 0);
    const lone = items.find(item => item.kind !== 'gift' && item.kind !== 'mystery');
    const picks = uniqueDesigns(promise ? reaching : candidates, finishWord(lone?.handle));
    if (!picks.length && !loading && !hidden) return '';
    const title = t('third_title', { price: prices.third });
    const unlock = promise ? t('third_unlock', { title: giftName(), value: money(gift.value) }) : '';
    // With two or more sets in the bag, which set it goes with.
    const sets = (offers.groups || []).filter(group => group.offer === 'pair').reduce((total, group) => total + (group.sets || 0), 0);
    const forSet = sets > 1 && target.names ? t('third_for', { title: target.names }) : '';
    // The set builder's gold offer card (assets/humbler-cart.css): "3RD PIECE" badge, "Only $29" over the struck
    // regular "from" price (the lowest regular price of the picks shown, once they are all in), the unlock line,
    // the picks. is-ready (picks loaded) starts its one sweep, so the loading → loaded re-render does not restart it.
    // With any 2-for-$99 piece the lowest price is a cheap bracelet's, not what $29 beats: no "from" then.
    const regular = loading || any ? 0 : Math.min(...picks.map(({ variant }) => variant.price));
    const only = esc(t('third_only')).replace('%price%', `<span class="hb-cart-nudge__amount">${esc(prices.third)}</span>`);
    const was = regular > cfg.third.price
      ? esc(t('third_from')).replace('%price%', `<s><span class="hb-sr">${esc(t('regular_price'))}</span>${esc(money(regular))}</s>`)
      : '';
    // The Ridge bag shows 4 picks on phones (assets/humbler-cart.css): "Show all 9" lists the rest of the choices, which
    // only this card prices at $29 (the button is not shown in Classic's scroller or the desktop offers panel). With any
    // 2-for-$99 piece it is "Show all pieces" (is-load), shown in every layout, as it also loads the rest of the list.
    const more = !thirdAll && !loading && (picks.length > 4 || hidden)
      ? `<button type="button" class="hb-cart-nudge__more${hidden ? ' is-load' : ''}" data-hb-cart-action="third-all">${esc(any ? t('third_all_any') : t('third_all', { number: picks.length }))}</button>`
      : '';
    return `<section class="hb-cart-nudge hb-cart-nudge--third${loading ? '' : ' is-ready'}" data-kind="third" aria-label="${esc([title, forSet, unlock].filter(Boolean).join('. '))}">
      <p class="hb-cart-nudge__title"><span class="hb-cart-nudge__badge">${esc(t('third_badge'))}</span><span class="hb-cart-nudge__offer"><strong>${only}</strong>${was ? ` <span class="hb-cart-nudge__was">${was}</span>` : ''}</span></p>
      ${forSet ? `<p class="hb-cart-nudge__meta">${esc(forSet)}</p>` : ''}
      ${unlock ? `<p class="hb-cart-nudge__meta">${esc(unlock)}</p>` : ''}
      <ul class="hb-cart-picker${thirdAll ? ' is-all' : ''}" role="list">${picks.map(({ product, variant, sizes, cost }) => pickHtml({
        product, variant, sizes, price: cost, compare: variant.price, action: 'add-third', focus: `third:${variant.id}`, label: t('third_add', { title: product.title, price: money(cost) }), bundle: target.bundle
      })).join('')}${loading ? skeletonPicks(Math.max(1, 3 - picks.length)) : ''}</ul>
      ${more}
    </section>`;
  }

  function pairNudge(items) {
    const eligible = items.filter(item => item.kind !== 'gift' && item.kind !== 'mystery' && pairHandles.has(item.handle));
    const lone = eligible.find(item => !(item.offer_labels || []).some(label => label === 'pair' || label === 'chains' || label === 'stack')) || eligible[0];
    if (!lone) return '';
    const inBag = new Set(items.map(item => item.handle));
    const owned = bagDesigns(items);
    const minimum = cfg.pair.price - lone.original_price;
    const data = suggestionData(lone);
    const config = engravingConfig(document);
    const candidates = data.products.filter(product => pairHandles.has(product.handle) && !inBag.has(product.handle) && !owned.has(designOf(product.title)) &&
      product.handle !== cfg.mystery.handle && product.handle !== cfg.gift.handle && product.handle !== protection.handle && product.available && personalKind(product, config) !== 'hidden')
      .map(product => ({ product, variant: firstAvailable(product), kind: personalKind(product, config) }))
      .filter(({ variant }) => variant && variant.price > minimum);
    const picks = uniqueDesigns(candidates, finishWord(lone.handle)).slice(0, 6);
    const title = t('pair_title', { price: prices.pair });
    const link = raw.pair?.page ? `<a class="hb-link hb-cart-nudge__link" href="${esc(raw.pair.page)}">${esc(t('pair_link'))}${icon('arrow')}</a>` : '';
    const list = picks.length || data.loading
      ? `<ul class="hb-cart-picker" role="list">${picks.map(({ product, variant, kind }) => pickHtml({
        product, variant, price: variant.price, compare: variant.compare_at_price || 0, kind,
        action: product.variants.length === 1 ? 'add-pair' : '', focus: `pair:${variant.id}`, label: t('pair_add', { title: product.title })
      })).join('')}${data.loading ? skeletonPicks(3) : ''}</ul>`
      : '';
    return `<section class="hb-cart-nudge" data-kind="pair" aria-label="${esc(title)}">
      <div class="hb-cart-nudge__row"><p class="hb-cart-nudge__title"><span>${esc(title)}</span></p>${link}</div>
      ${list}
    </section>`;
  }

  // When no offer needs a next step: related pieces for the first line, filled up from best sellers.
  function recsNudge(items) {
    const first = items.find(item => item.kind !== 'gift' && item.kind !== 'mystery');
    const data = first ? suggestionData(first) : { loading: false, products: [] };
    if (data.loading) {
      return `<section class="hb-cart-nudge" data-kind="recs" aria-label="${esc(t('complete_title'))}" aria-busy="true">
        <p class="hb-cart-nudge__title"><span>${esc(t('complete_title'))}</span></p>
        <ul class="hb-cart-picker" role="list">${skeletonPicks(3)}</ul>
      </section>`;
    }
    const inBag = new Set(items.map(item => item.handle));
    const owned = bagDesigns(items);
    const fallback = (settingsData.recommendations || []).map(normalizeProduct).filter(Boolean);
    const config = engravingConfig(document);
    const candidates = [...data.products, ...fallback].filter(product => product.available && !inBag.has(product.handle) && !owned.has(designOf(product.title)) &&
      product.handle !== cfg.mystery.handle && product.handle !== cfg.gift.handle && product.handle !== protection.handle && personalKind(product, config) !== 'hidden')
      .map(product => ({ product, variant: firstAvailable(product), kind: personalKind(product, config) }))
      .filter(({ variant }) => variant);
    const picks = uniqueDesigns(candidates, finishWord(first?.handle)).slice(0, 8);
    if (!picks.length) return '';
    return `<section class="hb-cart-nudge" data-kind="recs" aria-label="${esc(t('complete_title'))}">
      <p class="hb-cart-nudge__title"><span>${esc(t('complete_title'))}</span></p>
      <ul class="hb-cart-picker" role="list">${picks.map(({ product, variant, kind }) => pickHtml({
        product, variant, price: variant.price, compare: variant.compare_at_price || 0, kind,
        action: product.variants.length === 1 ? 'add-rec' : '', focus: `rec:${variant.id}`, label: t('add_label', { title: product.title })
      })).join('')}</ul>
    </section>`;
  }

  // What adding the Mystery piece really does to the total. The $29 third piece is judged on the spend before it, so
  // an $18 add-on can take that spend past the gift threshold and end the third-piece price (offer rule 3): then the
  // bag goes up by more than $18. Priced by the offer engine at regular prices, as the bag's own hints are (on
  // Shopify the store's discounts follow the same rules).
  function mysteryEffect(price) {
    if (!cfg || !window.HumblerOffers || !model) return null;
    const lines = engineLines(model.items);
    const options = { giftDeclined: !!model.offers?.gift?.declined, mysteryPrice: price };
    const paid = result => sum(result.lines.filter(line => line.kind !== 'gift').map(line => line.finalTotal));
    const before = window.HumblerOffers.price(lines, cfg, options);
    const after = window.HumblerOffers.price([...lines, { key: '\u0000mystery-add', handle: cfg.mystery.handle, variantId: cfg.mystery.variantId, price, quantity: 1 }], cfg, options);
    return { delta: paid(after) - paid(before) };
  }

  // Mystery add-on, kept tight (owner, 2026-10-17): a small square photo beside three one-line rows — the short title
  // (the product title is too long for one line); what it is, or, when adding it costs more than its price, what it
  // really adds and why; the price (struck value first) with the save badge — plus the unlock line when it would
  // unlock the gift; then one full-width Add with the price (on a /cart column 560px or wider, the Add sits at the right of
  // the row instead). "Add-on" stays in the section name for screen readers.
  function mysteryCard(offers) {
    const mystery = raw.mystery;
    if (!mystery?.variantId || mystery.available === false || offers.mystery.inCart) return '';
    const price = Number(mystery.price) || 0;
    const value = Number(mystery.compareAtPrice) || 0;
    const save = value > price ? Math.round((1 - price / value) * 100) : 0;
    const effect = mysteryEffect(price);
    const costs = effect && effect.delta > price ? effect : null;
    const unlocks = !costs && gift.enabled && offers.mystery.unlocksGift && !offers.gift.qualifies;
    const title = t('mystery_title');
    const notes = costs ? [t('mystery_effect_total', { amount: money(costs.delta) })] : [];
    return `<section class="hb-cart-addon hb-cart-addon--mystery" aria-label="${esc([t('mystery_eyebrow'), title].join(': '))}">
      <span class="hb-cart-addon__media hb-stage">${mystery.image ? `<img src="${esc(imageUrl(mystery.image, 160))}" width="64" height="64" alt="" loading="lazy">` : ''}</span>
      <div class="hb-cart-addon__main">
        <p class="hb-cart-addon__title"><span>${esc(title)}</span></p>
        ${notes.length ? notes.map((note, index) => `<p class="hb-cart-addon__note${index ? '' : ' hb-cart-addon__note--total'}">${esc(note)}</p>`).join('') : `<p class="hb-cart-addon__desc">${esc(t('mystery_desc'))}</p>`}
        ${unlocks ? `<p class="hb-cart-addon__hint">${icon('gift')}<span>${esc(t('mystery_hint', { title: giftName() }))}</span></p>` : ''}
        <p class="hb-cart-addon__price"><span class="hb-sr">${esc(t('price'))}</span><span${value > price ? ' class="hb-cart-addon__sale"' : ''}>${esc(money(price))}</span>${value > price ? `<s><span class="hb-sr">${esc(t('regular_price'))}</span>${esc(money(value))}</s>` : ''}${save ? `<span class="hb-cart-addon__save">${esc(t('mystery_save', { number: save }))}</span>` : ''}</p>
      </div>
      <button type="button" class="hb-button hb-button--block hb-cart-addon__add" data-hb-cart-action="add-mystery" data-hb-focus="mystery" aria-label="${esc([t('mystery_add', { title, price: money(price) }), ...notes].join('. '))}">${icon('plus')}<span>${esc(t('add'))}</span><span class="hb-button__sep" aria-hidden="true"></span><span class="hb-button__price">${esc(money(price))}</span></button>
    </section>`;
  }

  // One offers row ("Before you go" cards): the $29 third piece > "add one more" > "Complete your set". The drawer
  // drops "Complete your set" once the free gift is unlocked, so it goes from the bag to checkout; /cart keeps it.
  function nudgesHtml(items, root) {
    const offers = model.offers;
    if (!offers || !cfg) return '';
    let primary = '';
    // Every set can take its own $29 3rd (owner, 2026-10-19), whether or not it unlocks the gift.
    if (offers.third.available) primary = thirdNudge(offers, items);
    if (!primary && offers.pair.needsOne) primary = pairNudge(items);
    const drawer = root?.dataset.hbCartRoot === 'drawer';
    if (!primary && !(drawer && gift.enabled && offers.gift.qualifies)) primary = recsNudge(items);
    return primary;
  }

  /* ---------- actions ---------- */
  function normalizeItems(items) {
    return (Array.isArray(items) ? items : [items]).filter(Boolean).map(item => {
      const properties = item.properties && Object.keys(item.properties).length ? { ...item.properties } : null;
      // hint: what the caller knows about the piece, for the personalisation guard only (never sent to Shopify).
      const hint = { kind: item.kind, productId: item.productId ?? item.product_id, handle: item.handle, type: item.type ?? item.product_type };
      return { id: String(item.id ?? item.variantId ?? '').replace(/^gid:\/\/shopify\/ProductVariant\//, ''), quantity: Math.max(1, Math.floor(Number(item.quantity) || 1)), properties, hint };
    }).filter(item => /^\d+$/.test(item.id));
  }

  const describeAdd = list => next => {
    const item = next.items.find(line => String(line.variant_id) === String(list[0].id));
    return item ? t('status_added', { title: spokenTitle(item), total: money(next.total_price) }) : t('status_updated', { total: money(next.total_price) });
  };

  // /cart: is the bag's top already on screen, below the sticky header? (Then open() leaves the page where it is.)
  function pageTopVisible(page) {
    const headerBottom = Math.max(0, document.querySelector('.hb-header')?.getBoundingClientRect().bottom || 0);
    const top = page.getBoundingClientRect().top;
    return top >= headerBottom - 1 && top < window.innerHeight;
  }

  function open(trigger) {
    const page = document.querySelector('[data-hb-cart-page]');
    if (page) {
      page.querySelector('[data-hb-cart-page-heading]')?.focus({ preventScroll: true });
      // Scroll only when the bag's top is under the sticky header or off screen (scroll-margin-top in
      // assets/humbler-cart.css keeps it clear of the header); otherwise the page stays put.
      if (!pageTopVisible(page)) page.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    } else {
      const dialog = document.getElementById('hb-cart');
      if (dialog && !dialog.open) {
        drawerTrigger = trigger || document.activeElement;
        if (UI().openDialog) UI().openDialog(dialog, trigger);
        else dialog.showModal();
        dialog.querySelector('[data-hb-cart-heading]')?.focus({ preventScroll: true });
      }
    }
    return (ready || Promise.resolve()).then(() => model);
  }

  // An add that just completed a set: when the bag drawer is open and now offers the $29 3rd piece, scroll the bag
  // down to that card (just under the sticky offer strip), so the next step is in view. Once per new offer.
  function revealThird() {
    const frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : callback => setTimeout(callback, 16);
    frame(() => frame(() => {
      const dialog = document.getElementById('hb-cart');
      const card = dialog?.open ? dialog.querySelector('[data-kind="third"]') : null;
      const body = card?.closest('.hb-cart__body');
      if (!body) return;
      const sticky = body.querySelector('.hb-cart__progress-slot:not([hidden])')?.offsetHeight || 0;
      const top = card.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - sticky - 12;
      body.scrollTo({ top: Math.max(0, top), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }));
  }

  function close() {
    const dialog = document.getElementById('hb-cart');
    if (dialog?.open) {
      if (UI().closeDialog) UI().closeDialog(dialog);
      else dialog.close();
    }
    return Promise.resolve(model);
  }

  /* ---------- drawer layout: Classic or Ridge (docs/REBUILD-PLAN.md, "Bag layouts") ---------- */
  // Theme settings › Humbler offers › Bag layout: Liquid puts .hb-cart--ridge on the drawer for Ridge. A viewer can try
  // the other one: ?bag=ridge or ?bag=classic on any URL is kept in this browser and wins over the setting, and
  // ?bag=default forgets it. Applied at load, while the drawer is closed, so it never flashes.
  const LAYOUT_KEY = 'hb:bag-layout';
  const LAYOUTS = ['classic', 'ridge'];
  const local = {
    get: key => { try { return window.localStorage.getItem(key); } catch { return null; } },
    set: (key, value) => { try { window.localStorage.setItem(key, value); } catch { /* storage blocked */ } },
    remove: key => { try { window.localStorage.removeItem(key); } catch { /* storage blocked */ } }
  };
  function bagLayout(setting) {
    let asked = '';
    try { asked = new URLSearchParams(window.location?.search || '').get('bag') || ''; } catch { asked = ''; }
    if (asked === 'default') local.remove(LAYOUT_KEY);
    else if (LAYOUTS.includes(asked)) local.set(LAYOUT_KEY, asked);
    // The query applies to this page even where storage is blocked.
    const chosen = LAYOUTS.includes(asked) ? asked : local.get(LAYOUT_KEY);
    return LAYOUTS.includes(chosen) ? chosen : setting;
  }
  let ridge = false;
  function applyLayout() {
    const dialog = document.getElementById('hb-cart');
    if (!dialog) return;
    // The class Liquid printed is the setting (read before the toggle; a section reload in the editor prints it again).
    ridge = bagLayout(dialog.classList.contains('hb-cart--ridge') ? 'ridge' : 'classic') === 'ridge';
    dialog.classList.toggle('hb-cart--ridge', ridge);
    placeOffers();
    watchFoot(dialog);
  }

  // Ridge pins the bottom zone over the bag's scroll: keyboard focus and scrollIntoView stop above it (the body's
  // scroll-padding-bottom, assets/humbler-cart.css), at its real height, which grows where its copy wraps (small
  // phones, longer languages) and with the preview's checkout note.
  let footWatch = null;
  function watchFoot(dialog) {
    footWatch?.disconnect();
    footWatch = null;
    const foot = dialog.querySelector('.hb-cart__bottom');
    const body = dialog.querySelector('.hb-cart__body');
    if (!ridge || !foot || !body || typeof ResizeObserver !== 'function') return;
    footWatch = new ResizeObserver(() => body.style.setProperty('--hb-foot-h', `${foot.offsetHeight}px`));
    footWatch.observe(foot);
  }

  // Ridge from 1000px: the "Add & save" block ([data-hb-cart-offers]) moves into the offers panel to the left of the
  // bag, with its own scroll, while it holds a row of picks (or their bones); under 1000px, in Classic, and when the
  // Mystery card is its only offer, it sits in the bag before the summary, where Liquid put it (a 380px panel around
  // one card would be mostly empty, and the drawer would jump between 480 and 860px as the piece goes in and out).
  // Either way it stays inside the drawer root, so the click handler, renderRoot() and focus restore find its controls,
  // and revealThird() (which scrolls only the bag's body) leaves the panel alone. render() calls it after each render.
  const wide = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 1000px)') : null;
  const hasPicks = offers => {
    const nudges = offers.querySelector('[data-hb-cart-nudges]');
    return !!nudges && !nudges.hidden && nudges.children.length > 0;
  };
  function placeOffers() {
    const dialog = document.getElementById('hb-cart');
    const offers = dialog?.querySelector('[data-hb-cart-offers]');
    const panel = dialog?.querySelector('[data-hb-cart-offers-panel]');
    const body = dialog?.querySelector('.hb-cart__body');
    if (!offers || !panel || !body) return;
    const target = ridge && wide?.matches && hasPicks(offers) ? panel : body;
    if (offers.parentNode !== target) {
      // Moving a node drops focus: a control in the block keeps it.
      const active = document.activeElement;
      const kept = !!active && offers.contains(active);
      if (target === panel) panel.append(offers);
      else body.insertBefore(offers, body.querySelector(':scope > [data-hb-cart-summary]'));
      syncOffersPanel(); // un-hide the panel first: focus() into a [hidden] (display: none) panel does nothing
      if (kept) active.focus({ preventScroll: true });
    }
    syncOffersPanel();
  }
  // The panel shows only while the offers block is in it; without it the bag keeps its single 480px column.
  function syncOffersPanel() {
    const panel = document.querySelector('#hb-cart [data-hb-cart-offers-panel]');
    if (!panel) return;
    const offers = panel.querySelector('[data-hb-cart-offers]');
    const filled = !!offers && hasPicks(offers);
    if (panel.hidden === filled) panel.hidden = !filled;
  }
  if (wide?.addEventListener) wide.addEventListener('change', placeOffers);
  else wide?.addListener?.(placeOffers);

  function change(key, quantity) {
    const item = model?.items.find(line => line.key === key);
    const target = Math.max(0, Math.floor(Number(quantity) || 0));
    return run(current => current.change(key, target), next => {
      const title = spokenTitle(item);
      if (!title) return t('status_updated', { total: money(next.total_price) });
      return target === 0 ? t('status_removed', { title, total: money(next.total_price) }) : t('status_quantity', { title, quantity: target, total: money(next.total_price) });
    });
  }

  const api = {
    add(items, options = {}) {
      const list = normalizeItems(items);
      if (!list.length) return Promise.resolve({ ok: false, error: t('error_unknown'), cart: model });
      // Personalisation guard (refusal()): nothing is posted; the caller shows the message ('engraving': on the
      // piece's own page, add the engraving; 'personalise': from a one-tap path, personalise it on its page).
      const refused = refusal(list, options.source, engravingConfig(document), offerVariantIds(raw), window.HumblerEngraving);
      if (refused) {
        const key = refused === 'engraving' ? 'engraving_missing' : 'engraving_required';
        return Promise.reject(Object.assign(failure(refused, strings[key] ? t(key) : t('error_generic')), { ok: false, cart: model }));
      }
      if (options.source === 'third' && cfg) {
        for (const item of list) {
          item.quantity = 1;
          // Same values as live (USD, whatever the shopper's currency): the store's discount reads them.
          item.properties = { ...(item.properties || {}), _bundle_upsell: UI().moneyUsd ? UI().moneyUsd(cfg.third.priceUsd) : prices.third, _bundle_upsell_cents: String(cfg.third.priceUsd) };
        }
      }
      const thirdBefore = !!document.querySelector('#hb-cart [data-kind="third"]');
      return run(current => current.add(list, options), describeAdd(list)).then(result => {
        if (options.open !== false) open(options.trigger);
        if (result?.ok && options.source !== 'third' && !thirdBefore) revealThird();
        return result;
      });
    },
    open,
    close,
    refresh: () => run(current => current.getCart()).then(result => result.cart),
    change,
    remove: key => change(key, 0),
    // Removes several lines at once ("Remove set"); same result shape as change().
    removeLines(keys) {
      const list = [...new Set((Array.isArray(keys) ? keys : [keys]).map(String))].filter(key => model?.items.some(item => item.key === key));
      if (!list.length) return Promise.resolve({ ok: false, error: t('error_generic'), cart: model });
      return run(async current => {
        if (current.removeMany) return current.removeMany(list);
        let next = model;
        for (const key of list) next = await current.change(key, 0);
        return next;
      }, next => t('status_removed_set', { total: money(next.total_price) }));
    },
    restoreGift: () => run(current => current.restoreGift(), next => t('status_updated', { total: money(next.total_price) })),
    getState: () => Promise.resolve({ cart: model, busy: busy > 0, adapter: getAdapter().name })
  };
  window.HumblerCart = api;

  /* ---------- events ---------- */
  // A 3rd-piece pick's size menu: its Add adds the size chosen, and the choice holds through re-renders.
  document.addEventListener('change', event => {
    const menu = event.target.closest?.('[data-hb-pick-size]');
    if (!menu || !menu.closest('[data-hb-cart-root]')) return;
    pickSizes.set(menu.dataset.handle, menu.value);
    const add = menu.closest('.hb-cart-pick')?.querySelector('[data-hb-cart-action="add-third"]');
    if (add) add.dataset.variantId = menu.value;
  });
  document.addEventListener('click', event => {
    const control = event.target.closest('[data-hb-cart-action]');
    if (!control || !control.closest('[data-hb-cart-root]') || control.disabled) return;
    const action = control.dataset.hbCartAction;
    const item = model?.items.find(line => line.key === control.dataset.key);
    const line = control.closest('.hb-cart-line, .hb-cart-set');
    const pending = promise => {
      control.classList.add('is-loading');
      line?.classList.add('is-pending');
      promise.finally(() => {
        control.classList.remove('is-loading');
        line?.classList.remove('is-pending');
      });
    };
    if (action === 'increase' && item) pending(change(item.key, item.quantity + 1));
    else if (action === 'decrease' && item) pending(change(item.key, Math.max(1, item.quantity - 1)));
    else if (action === 'remove' && item) pending(change(item.key, 0));
    else if (action === 'remove-set') {
      let keys = [];
      try { keys = JSON.parse(control.dataset.keys || '[]'); } catch { keys = []; }
      if (keys.length) pending(api.removeLines(keys));
    }
    else if (action === 'restore-gift') pending(api.restoreGift());
    else if (action === 'add-third') pending(guarded(api.add([{ id: control.dataset.variantId, quantity: 1, ...(control.dataset.bundle ? { properties: { _bundle_id: control.dataset.bundle } } : {}) }], { source: 'third', open: false })));
    else if (action === 'add-pair' || action === 'add-rec') {
      const hint = { kind: control.dataset.hbKind, productId: control.dataset.productId, handle: control.dataset.handle };
      pending(guarded(api.add([{ id: control.dataset.variantId, quantity: 1, ...hint }], { source: 'card', open: false })));
    }
    else if (action === 'add-mystery' && raw.mystery?.variantId) pending(guarded(api.add([{ id: raw.mystery.variantId, quantity: 1 }], { source: 'mystery', open: false })));
    else if (action === 'third-all') {
      thirdAll = true;
      const root = control.closest('[data-hb-cart-root]');
      render();
      // The button is gone: focus goes to the first pick it showed (scrolled into view, clear of the pinned footer).
      root.querySelector('[data-kind="third"] .hb-cart-picker > :nth-child(5) .hb-cart-pick__title')?.focus();
    }
  });
  // A bag pick the personalisation guard refused (only when the page is misconfigured: personalised picks are links):
  // say why in the bag's notice line and status region.
  function guarded(promise) {
    return promise.catch(error => {
      noticeText = error?.message || t('error_generic');
      roots().forEach(renderNotice);
      announce(noticeText);
      return { ok: false, error: noticeText, cart: model };
    });
  }

  // Product-card quick add (snippets/humbler-card.liquid), also inside a size [popover].
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-hb-quick-add]');
    if (!button || button.disabled || button.classList.contains('is-loading')) return;
    event.preventDefault();
    const popover = button.closest('[popover]');
    const trigger = (popover && document.querySelector(`[popovertarget="${CSS.escape(popover.id)}"]`)) || button;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    // data-hb-kind="plain": the page classified the piece (snippets/humbler-personalised), for the guard.
    let result;
    try {
      result = await api.add([{ id: button.dataset.variantId, quantity: 1, kind: button.dataset.hbKind, handle: button.dataset.handle }], { source: 'card', open: false });
    } catch (error) {
      // Refused by the personalisation guard: the piece is personalised on its own page, so go there.
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      if (popover?.matches(':popover-open')) popover.hidePopover();
      announce(error?.message || t('error_generic'));
      if (button.dataset.handle) location.assign(`${route('products/')}${encodeURIComponent(button.dataset.handle)}`);
      return;
    }
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    if (popover?.matches(':popover-open')) popover.hidePopover();
    if (result.ok && !trigger.classList.contains('is-added')) {
      const original = trigger.innerHTML;
      trigger.classList.add('is-added');
      if (icon('check')) trigger.innerHTML = icon('check');
      setTimeout(() => { trigger.classList.remove('is-added'); trigger.innerHTML = original; }, 1600);
    }
    open(trigger);
  });

  // Plain links to /cart open the drawer (the drawer's own "View cart" link opts out).
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('data-hb-cart-page-link') || link.target === '_blank' || document.querySelector('[data-hb-cart-page]')) return;
    const href = new URL(link.href, location.href);
    if (href.origin !== location.origin || href.search || href.pathname.replace(/\/$/, '') !== route('cart').replace(/\/$/, '')) return;
    if (!document.getElementById('hb-cart')) return;
    event.preventDefault();
    open(link);
  });

  // The preview note next to the checkout that was used: the top Checkout, the footer / summary pair (each a
  // data-hb-cart-checkout-zone), else the bag's first.
  const previewNote = control => (control?.closest('[data-hb-cart-checkout-zone]') || control?.closest('[data-hb-cart-root]'))?.querySelector('[data-hb-cart-preview-note]');

  // Checkout+ status line: said only when the bag gained or lost the protection line.
  const plusDescribe = (next, previous) => {
    const now = !!next.protection?.inCart;
    return now === !!previous?.protection?.inCart ? '' : t(now ? 'checkout_plus_added' : 'checkout_plus_removed');
  };
  const plusReady = new WeakSet();

  // Checkout waits for pending bag changes. "Checkout+" / "Checkout without …" (data-hb-checkout-plus on / off) first add
  // or remove the protection line, then submit the same form with the same button again, so the native POST /cart
  // (name=checkout) and the preview guard below run as for a plain Checkout. A failed change stops there (run() shows
  // why). In the local preview it never leaves the page: the click shows a note.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-hb-cart-checkout-form]')) return;
    if (busy > 0) { event.preventDefault(); return; }
    const submitter = event.submitter;
    const intent = submitter?.dataset.hbCheckoutPlus;
    if (protection.enabled && (intent === 'on' || intent === 'off') && !plusReady.has(form) && typeof getAdapter().setProtection === 'function') {
      event.preventDefault();
      run(current => current.setProtection(intent === 'on'), plusDescribe).then(result => {
        if (!result.ok) return;
        // A task later: the preview bag can answer within this event's microtasks, while the browser still dispatches
        // the first submit and ignores a requestSubmit() made during it. Safari before 16 has no requestSubmit: a click
        // on the same button (the sticky bar's too, through form=) submits the same form the same way.
        setTimeout(() => {
          plusReady.add(form);
          try {
            if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
            else submitter.click();
          } finally { plusReady.delete(form); }
        });
      });
      return;
    }
    if (checkoutEnabled()) return;
    event.preventDefault();
    const note = previewNote(form);
    if (note) {
      note.hidden = false;
      clearTimeout(note._hbTimer);
      note._hbTimer = setTimeout(() => { note.hidden = true; }, 5000);
      if (event.submitter?.closest('[data-hb-cart-bar]')) note.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
    }
    announce(t('checkout_preview'));
  });

  // Checkout+ "(i)": the Checkout+ pop-up (a sibling of the drawer, so /cart has it too). HumblerUI
  // returns focus to one opener only, so when it opens over the drawer, the drawer's own opener gets focus back when
  // the drawer closes.
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-hb-checkout-plus-info]');
    const dialog = button && document.getElementById(button.getAttribute('aria-controls'));
    if (!dialog || dialog.open) return;
    event.preventDefault();
    const drawer = document.getElementById('hb-cart');
    if (drawer?.open && !drawer._hbPlusReturn) {
      const back = drawerTrigger;
      drawer._hbPlusReturn = true;
      drawer.addEventListener('close', () => {
        drawer._hbPlusReturn = false;
        if (back && document.contains(back)) back.focus({ preventScroll: true });
      }, { once: true });
    }
    if (UI().openDialog) UI().openDialog(dialog, button);
    else dialog.showModal();
  });

  // /cart on mobile: a sticky Total + Checkout bar while neither the top Checkout nor the summary's is on screen.
  (() => {
    const page = document.querySelector('[data-hb-cart-page]');
    const bar = page?.querySelector('[data-hb-cart-bar]');
    const targets = [...(page?.querySelectorAll('[data-hb-cart-top-checkout], [data-hb-cart-summary-checkout]') || [])];
    if (!bar || !targets.length || !('IntersectionObserver' in window)) return;
    const visible = new Map(targets.map(target => [target, true]));
    updateBar = () => { bar.hidden = [...visible.values()].some(Boolean) || !model?.items.length; };
    const observer = new IntersectionObserver(entries => {
      // A hidden top Checkout (desktop) never intersects, so it never keeps the bar away.
      entries.forEach(entry => visible.set(entry.target, entry.isIntersecting));
      updateBar();
    });
    targets.forEach(target => observer.observe(target));
  })();

  // Apps and other product forms can announce cart changes with these events.
  document.addEventListener('cart:change', event => {
    api.refresh().then(() => { if (event.detail?.baseEvent === 'variant:add') open(); });
  });
  document.addEventListener('cart:refresh', () => { api.refresh(); });
  window.addEventListener('pageshow', event => { if (event.persisted) api.refresh(); });
  document.addEventListener('shopify:section:load', event => {
    if (!event.target.querySelector('[data-hb-cart-root]')) return;
    event.target.querySelectorAll('[data-hb-cart-lines], [data-hb-cart-nudges]').forEach(node => { node._hbHtml = null; });
    // A reloaded drawer comes back with the setting's layout and the offers in the body.
    applyLayout();
    render();
  });

  /* ---------- first paint ---------- */
  // Until the first model arrives, a bag with items shows skeletons (offer strip, set cards and rows, Mystery row,
  // carousel, value rows) instead of the empty state. Liquid prints them from the server cart
  // (snippets/humbler-cart-skeleton); the preview bag lives in session storage, so it gets them here from the
  // drawer's templates, laid out by pendingPlan() with the same rules as the snippet's 'lines' part.
  function pendingPlan() {
    const current = getAdapter();
    const preview = !!current.storageKey;
    let lines = [];
    // A Checkout+ line is never a bag row (merchandise()), so it plans no skeleton and no spend.
    if (preview) {
      try { lines = JSON.parse(sessionStorage.getItem(current.storageKey) || 'null')?.lines || []; } catch { lines = []; }
      lines = lines.filter(line => line && Number(line.quantity) > 0 && !isProtectionLine({ variant_id: line.variantId }))
        .map(line => ({ key: line.key, quantity: Number(line.quantity), kind: line.kind || 'product', handle: line.handle || '', properties: line.properties || {}, variantId: String(line.variantId), final: null }));
    } else {
      lines = (readJson('[data-hb-cart-initial]')?.items || []).filter(item => !isProtectionLine(item)).map(item => ({
        key: item.key, quantity: item.quantity, kind: isGiftLine(item) ? 'gift' : item.properties?._bundle_upsell ? 'third' : 'product', handle: item.handle || '',
        properties: item.properties || {}, variantId: String(item.variant_id), discounted: item.final_line_price < item.original_line_price, final: item.final_line_price,
        // The chain deal's discount is titled "3 chains for $99" (scripts/supaeasy/three-chains-99.js).
        chainDeal: item.final_line_price < item.original_line_price && (item.line_level_discount_allocations || []).some(entry => /chain/i.test(String(entry.discount_application?.title || '')))
      }));
    }
    // Close to lineEntries() (snippets/humbler-cart-skeleton 'lines' does the same): the chain deal's lines one card;
    // each set the shopper built one card (its _bundle_id's 2-for lines once one is discounted, a $27 3rd at its own
    // price among them: every built set is a $99 set); a loose 3rd (a discounted line at the 3rd price) joins a built set
    // that has no 3rd yet, the other loose discounted lines are the extra sets (two each) and take the 3rds left; a loose
    // piece under the 3rd price fills a set's empty 3rd slot; the rest is rows. Without prices (the preview bag, or
    // another currency) every loose deal line counts alike: one joins each built set of 2, the rest make ceil(n / 3)
    // extra sets, the first ones with their 3rd. The
    // preview bag is unpriced, so it assumes each offer applies (the chain deal from 3 chains, a set from 2 pieces).
    const isMystery = line => !!cfg && line.variantId === String(cfg.mystery.variantId);
    const discounted = line => (preview ? true : line.discounted);
    const units = group => sum(group.map(line => line.quantity));
    const giftLine = lines.some(line => line.kind === 'gift');
    const paid = lines.filter(line => line.kind !== 'gift');
    const used = new Set();
    const chainHandles = new Set(cfg?.chains.handles || []);
    const size = cfg?.chains.count || 3;
    const chainPool = paid.filter(line => chainHandles.has(line.handle));
    const chainLines = preview ? (units(chainPool) >= size ? chainPool : []) : paid.filter(line => line.chainDeal);
    chainLines.forEach(line => used.add(line));
    // The deal's pieces: every 2-for piece but the Mystery (the store leaves it out) and the chain deal's chains.
    const eligible = paid.filter(line => !used.has(line) && !isMystery(line) && (pairHandles.has(line.handle) || line.kind === 'third'));
    const deal = preview ? (units(eligible) >= 2 ? eligible : []) : eligible.filter(line => line.discounted);
    const dealUnits = units(deal);
    const sets = [];
    const bundles = new Map();
    for (const line of eligible) {
      const id = line.properties._bundle_id;
      if (id) bundles.set(id, [...(bundles.get(id) || []), line]);
    }
    const built = [...bundles.values()].filter(group => group.length > 1 && group.some(discounted)).map(group => [...group]);
    built.forEach(group => group.forEach(line => used.add(line)));
    // A loose line Shopify prices at the 3rd price is a 3rd (USD prices only: elsewhere the conversion rounds).
    const priced = !preview && !localized();
    const atThird = line => priced && line.quantity === 1 && line.final === cfg.third.price;
    const looseThirds = deal.filter(line => !used.has(line) && atThird(line));
    const loosePieces = deal.filter(line => !used.has(line) && !atThird(line));
    // A built set with 2 pieces takes a loose 3rd (unpriced: any loose deal line).
    const attach = priced ? looseThirds : loosePieces;
    for (const group of built) {
      if (attach.length && units(group) === 2) group.push(attach.shift());
    }
    // The extra sets: two discounted pieces each and the 3rds left (by price), or, unpriced, ceil(n / 3) of them.
    const pool = loosePieces;
    const extra = pool.length > 1 ? (priced ? Math.floor(pool.length / 2) : Math.ceil(pool.length / 3)) : 0;
    const extraSets = Array.from({ length: extra }, () => []);
    if (extra) {
      if (priced) pool.forEach((line, index) => extraSets[Math.min(extra - 1, Math.floor(index / 2))].push(line));
      else pool.forEach((line, index) => extraSets[index < 2 * extra ? Math.floor(index / 2) : index - 2 * extra].push(line));
      for (const group of extraSets) {
        if (looseThirds.length && units(group) === 2) group.push(looseThirds.shift());
      }
    }
    const placed = [...built, ...extraSets];
    placed.flat().forEach(line => used.add(line));
    // A loose piece under the 3rd price ($27 bracelet, not discounted) fills a set's empty 3rd slot.
    const cheap = priced ? paid.filter(line => !used.has(line) && eligible.includes(line) && line.quantity === 1 && line.final < cfg.third.price) : [];
    for (const group of placed) {
      if (cheap.length && units(group) === 2) {
        const line = cheap.shift();
        group.push(line);
        used.add(line);
      }
    }
    placed.forEach(group => sets.push(group.length));
    // Sets with no 3rd yet: the bag offers each its own $29 3rd (the carousel, after the gift too).
    const open = placed.filter(group => units(group) === 2).length;
    if (chainLines.length > 1) sets.push(chainLines.length);
    else chainLines.forEach(line => used.delete(line));
    const rest = paid.filter(line => !used.has(line));
    // Spend: the server cart's own prices; for the preview bag, a set of 2 or 3 at its price, the chain deal at a
    // chain's price each and Mystery lines (known only when every other paid unit is in one of them).
    const mysteryPrice = Number(raw.mystery?.price) || 0;
    let spend = null;
    if (!preview) spend = sum(paid.map(line => line.final || 0));
    else if (cfg && (dealUnits === 0 || dealUnits === 2 || dealUnits === 3) && paid.every(line => isMystery(line) || deal.includes(line) || chainLines.includes(line))) {
      spend = (dealUnits ? cfg.pair.price : 0) + (dealUnits === 3 ? cfg.third.price : 0) + units(chainLines) * cfg.chains.unitPrice + sum(paid.filter(isMystery).map(line => line.quantity * mysteryPrice));
    }
    const threshold = cfg?.gift.threshold || Infinity;
    const qualifies = gift.enabled && (preview ? spend !== null && spend >= threshold : giftLine);
    const addon = !!(cfg && raw.mystery?.variantId && raw.mystery.available !== false && !paid.some(isMystery));
    const below = gift.enabled && spend !== null && spend < threshold;
    // A lone 2-for piece: the "add one more" carousel, which the drawer keeps after the gift too (pair.needsOne).
    const needsOne = units(eligible) === 1;
    let saved = null;
    try { saved = JSON.parse(session.get(SHAPE_KEY) || 'null'); } catch { saved = null; }
    const sig = bagSignature(paid);
    const base = {
      sets, rows: rest.length + (qualifies || giftLine ? 1 : 0), addon,
      // The Mystery row's second line: it unlocks the gift.
      hint: addon && below && spend + mysteryPrice >= threshold,
      // The 3rd-piece carousel's meta line: which set it is for (two sets or more), or a 3rd would reach the gift.
      meta: open > 0 && (placed.length > 1 || (below && dealUnits === 2 && spend + cfg.third.price >= threshold)),
      // summary(): the deal's sets and, with 3 or more pieces, their 3rds; the chain deal; the gift.
      // (the 3rds' row only when a discounted 3rd saved money: more discounted deal units than two a set)
      values: 2 + (deal.some(discounted) ? 1 : 0) + (dealUnits > 2 * placed.length && placed.length && deal.some(discounted) ? 1 : 0) + (chainLines.length ? 1 : 0) + (qualifies || giftLine ? 1 : 0)
    };
    // Per surface: the shape this tab last rendered for the same lines (the other surface's when this one has not
    // rendered yet: same lines and Mystery row; /cart always has a carousel, the drawer drops "Complete your set"
    // once the gift is unlocked), else the rules above.
    const known = saved?.sig === sig ? saved : null;
    return {
      count: paid.length,
      // Each single row's engraving ('' for none), in the order lineEntries() draws single rows: the other lines,
      // then Mystery lines; the gift row, last, has none.
      engravings: [...rest.filter(line => !isMystery(line)), ...rest.filter(isMystery)].map(line => engravingOf(line.properties)),
      // Shop Pay installments ($50 and up): the preview bag's total is unknown until priced, so it is reserved
      // unless the known spend is under $50.
      installments: known ? !!known.installments : !!settingsData.installments && !localized() && (spend === null || spend >= 5000),
      shape: surface => known?.[surface]
        || (known?.drawer && { ...known.drawer, nudge: true })
        || (known?.page && { ...known.page, nudge: known.page.nudge && (known.page.kind !== 'recs' || !known.gift) })
        || { ...base, nudge: surface !== 'drawer' || !qualifies || needsOne || open > 0 }
    };
  }
  function renderPending() {
    // Shop Pay installments from the server cart straight away (both sections print it in Liquid; /cart with the
    // drawer setting's default, so this also covers "pay in 2"), so the summary does not grow when the bag renders.
    const initial = getAdapter().storageKey ? null : merchandise(readJson('[data-hb-cart-initial]'));
    const installments = initial?.items?.length ? installmentsText(initial.total_price) : '';
    if (installments) {
      document.querySelectorAll('[data-hb-cart-root] [data-hb-cart-installments][hidden]').forEach(node => {
        node.textContent = installments;
        node.hidden = false;
      });
    }
    const part = name => document.querySelector(`template[data-hb-cart-skeleton="${name}"]`)?.content;
    if (!part('line') || !part('set')) return;
    const plan = pendingPlan();
    if (!plan.count) return;
    // The preview bag's installments line (amount unknown until it is priced): a bone in the line's own box
    // (marked like the other skeletons, so a failed first load clears it and showError hides the line).
    if (!initial && plan.installments) {
      document.querySelectorAll('[data-hb-cart-root] [data-hb-cart-installments][hidden]').forEach(node => {
        node.innerHTML = '<span class="hb-skeleton hb-skeleton--text" data-hb-skeleton style="--w: 78%; --h: 10px"></span>';
        node.hidden = false;
      });
    }
    const shapes = {};
    // A row for a line with an engraving gets the real row's layout (the stepper grid) and its engraving text, so
    // the bag does not move when it renders (snippets/humbler-cart-skeleton prints the same for the server cart).
    const lineNode = engraving => {
      const node = part('line').cloneNode(true);
      const row = node.querySelector('.hb-cart-line');
      if (engraving && row) {
        row.classList.add('hb-cart-line--qty', 'hb-cart-line--prop');
        const prop = document.createElement('span');
        prop.className = 'hb-cart-line__prop';
        prop.textContent = t('engraving_line', { value: engraving });
        // under the detail line, above the stepper / price foot (as lineHtml() draws it)
        const foot = row.querySelector('.hb-cart-line__foot');
        if (foot) foot.before(prop);
        else row.querySelector('.hb-cart-line__main')?.append(prop);
      }
      return node;
    };
    const setNode = count => {
      const node = part('set').cloneNode(true);
      const list = node.querySelector('.hb-cart-set__lines');
      const piece = list?.firstElementChild;
      for (let index = 1; piece && index < count; index += 1) list.append(piece.cloneNode(true));
      return node;
    };
    for (const root of roots()) {
      const list = root.querySelector('[data-hb-cart-lines]');
      if (!list || list.children.length) continue;
      root.classList.add('is-pending');
      root.dataset.empty = 'false';
      root.querySelectorAll('[data-hb-cart-filled]').forEach(node => { node.hidden = false; });
      root.querySelectorAll('[data-hb-cart-empty]').forEach(node => { node.hidden = true; });
      const surface = root.dataset.hbCartRoot;
      const shape = (shapes[surface] ||= plan.shape(surface));
      shape.sets.forEach(count => list.append(setNode(count)));
      for (let index = 0; index < shape.rows; index += 1) list.append(lineNode(plan.engravings[index]));
      // The preview bag's sets save money: its summary keeps the "You save" line's room (blank until priced, like
      // the totals), so the summary does not grow when it renders. The server cart prints its own.
      if (!initial && shape.sets.length) root.querySelectorAll('[data-hb-cart-save][hidden]').forEach(node => { node.hidden = false; });
      const slot = root.querySelector('[data-hb-cart-progress-slot]');
      if (slot && !slot.children.length && part('progress')) slot.append(part('progress').cloneNode(true));
      root.querySelectorAll('[data-hb-cart-addon]').forEach(node => {
        if (node.children.length || !shape.addon || !part('addon')) return;
        const addon = part('addon').cloneNode(true);
        if (!shape.hint) addon.querySelectorAll('[data-hb-skeleton-hint]').forEach(hint => hint.remove());
        node.append(addon);
      });
      root.querySelectorAll('[data-hb-cart-nudges]').forEach(node => {
        if (node.children.length || !part('nudges') || !shape.nudge) return;
        const nudge = part('nudges').cloneNode(true);
        if (!shape.meta) nudge.querySelectorAll('[data-hb-skeleton-meta]').forEach(meta => meta.remove());
        node.append(nudge);
      });
      // The summary card's value rows (drawer and /cart).
      root.querySelectorAll('[data-hb-cart-value]').forEach(node => {
        if (node.children.length || !part('value')) return;
        const value = part('value').cloneNode(true);
        const rows = value.querySelector('.hb-cart-value');
        const row = rows?.firstElementChild;
        for (let index = 1; row && index < Math.min(7, shape.values || 2); index += 1) rows.append(row.cloneNode(true));
        node.append(value);
      });
    }
  }

  /* ---------- start ---------- */
  applyLayout();
  renderPending();
  placeOffers();
  ready = run(current => current.getCart()).then(result => result.cart);
  getAdapter().subscribe?.(next => commit(next));
})();
